import {
  AppBar,
  Box,
  Checkbox,
  Divider,
  Grid,
  LinearProgress,
  Radio,
  Tab,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { cloneDeep, debounce } from 'lodash-es';
import MaterialTable, { Column, Query, QueryResult } from 'material-table';
import moment from 'moment-timezone';
import React from 'react';

import { DashboardEventTypes, track } from '~/src/dash/analytics/analytics';
import { getBidderGroups, IBidderGroup } from '~/src/dash/api/bidder-group';
import { getChannelGroupsPromise } from '~/src/dash/api/channel-group';
import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import {
  DateGroup,
  DateGroupDays,
  DateGroupMonths,
  DateGroupWeeks,
  getDateGroupNumber,
  getValidDateGroup,
  IAnalytics,
  IChannelGroup,
  IHeaderBidder,
  IUser,
  queryHeaderBidders,
} from '~/src/dash/api/console-api';
import {
  dashboardCompareKeyAudibleViewableCustomQ2,
  dashboardCompareKeyAudibleViewableIndStdQ4,
  dashboardCompareKeyAVOCGroupMRate,
  dashboardCompareKeyAVOCRate,
  dashboardCompareKeyBlockedAds,
  dashboardCompareKeyBlockedRate,
  dashboardCompareKeyEligibleAds,
  dashboardCompareKeyImpressionsRendered,
  dashboardCompareKeyInvalidTrafficAds,
  dashboardCompareKeyInvalidTrafficRate,
  dashboardCompareKeyMeasuredAds,
  dashboardCompareKeyMeasuredRate,
  dashboardCompareKeyRenderRate,
  dashboardCompareKeyTrackedAds,
  dashboardCompareKeyViewableImpressions,
  dashboardCompareKeyViewableRate,
  dashboardKeyAudibleViewableCustomQ2,
  dashboardKeyAudibleViewableIndStdQ4,
  dashboardKeyAVOCGroupMRate,
  dashboardKeyAVOCRate,
  dashboardKeyBlockedAds,
  dashboardKeyBlockedRate,
  dashboardKeyEligibleAds,
  dashboardKeyImpressionsRendered,
  dashboardKeyInvalidTrafficAds,
  dashboardKeyInvalidTrafficRate,
  dashboardKeyMeasuredAds,
  dashboardKeyMeasuredRate,
  dashboardKeyRenderRate,
  dashboardKeyTrackedAds,
  dashboardKeyViewableImpressions,
  dashboardKeyViewableRate,
  dashboardTitleAudibleViewableCustomQ2,
  dashboardTitleAudibleViewableIndStdQ4,
  dashboardTitleAVOCGroupMRate,
  dashboardTitleAVOCRate,
  dashboardTitleBlockedAds,
  dashboardTitleBlockedRate,
  dashboardTitleEligibleAds,
  dashboardTitleImpressionsRendered,
  dashboardTitleInvalidTrafficAds,
  dashboardTitleInvalidTrafficRate,
  dashboardTitleMeasuredAds,
  dashboardTitleMeasuredRate,
  dashboardTitleRenderRate,
  dashboardTitleTrackedAds,
  dashboardTitleViewableImpressions,
  dashboardTitleViewableRate,
  FirstOrThirdParty,
  getFirstOrThirdPartyDeviceTypes,
  getFirstOrThirdPartyDeviceTypesAsIMenu,
} from '~/src/dash/api/ias-wrapper-api/constants';
import {
  getFirstAndThirdPartyIasData,
  IIASFirstAndThirdPartyAnalytics,
  IIASFirstAndThirdPartyRequestBody,
  queryFirstAndThirdPartyIasData,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import { EBool } from '~/src/dash/api/types/bool';
import { Calendar } from '~/src/dash/components/calendar/calendar';
import { IFocusedInput } from '~/src/dash/components/calendar/calendar-wrapper';
import { DashboardCards } from '~/src/dash/components/dashboard/dashboard-cards';
import { IGraphDataTypes } from '~/src/dash/components/dashboard/dashboard-graph';
import { DashboardGraphV2 } from '~/src/dash/components/dashboard/dashboard-graph-v2';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { MenuButton } from '~/src/dash/components/menu-button';
import { TabsWithBlueIndicator } from '~/src/dash/components/model-tab';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { ISelectedMetric } from '~/src/dash/globals/constants';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { ISelectedMetrics } from '~/src/dash/types/metrics';
import {
  IBaseState,
  IHistoryProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { ICalendarQuickSelectorOptions } from '~/src/dash/utils/calendar';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  formatDateFromUTCToOffset,
  formatDateStartDay,
  formatWithoutHoursMins,
  getStartDate,
} from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import {
  formatHugeNumberToShortenedString,
  formatRateToTwoDecimals,
} from '~/src/dash/utils/format';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import {
  alignRight,
  backgroundColor,
  blackColor,
  container,
  drawerWidth,
  primaryColor,
  whiteColor,
} from '~/src/dash/utils/material-dashboard-react';
import { toPercentage } from '~/src/dash/utils/math';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { isFirstPartyEnabled } from '~/src/dash/utils/ui';
import { FilterActiveChip } from '~/src/dash/views/campaign/campaign-settings-list-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export type FirstAndThirdPartyDashboardCardDataTitles =
  | 'Tracked Ads'
  | 'Impressions Rendered'
  | 'Render Rate'
  | 'Invalid Traffic Ads'
  | 'Invalid Traffic Rate'
  | 'Eligible Ads'
  | 'Measured Ads'
  | 'Measured Rate'
  | 'Viewable Impressions'
  | 'Viewable Rate'
  | 'Blocked Ads'
  | 'Blocked Rate'
  | 'Valid Traffic'
  | 'Non-Viewable Ads'
  | 'Passed Ads'
  | 'AVOC GroupM Count'
  | 'AVOC Count'
  | 'AVOC GroupM Rate'
  | 'AVOC Rate';

export type FirstAndThirdPartyDashboardCardDataKeys =
  | 'TrackedAds'
  | 'RenderedImpressions'
  | 'RenderRate'
  | 'IVTAds'
  | 'IVTRate'
  | 'EligibleAds'
  | 'MeasuredAds'
  | 'MeasuredRate'
  | 'ViewableImpressions'
  | 'ViewabilityRate'
  | 'AdBlockCount'
  | 'AdBlockRate'
  | 'SuspiciousImps'
  | 'UnsuspiciousImps'
  | 'UnviewableImps'
  | 'BlockedImps'
  | 'PassedAds'
  | 'AudibleViewableCustomQ2'
  | 'AudibleViewableIndStdQ4'
  | 'AVOCGroupMRate'
  | 'AVOCRate';

export type FirstAndThirdPartyDashboardCardDataCompareKeys =
  | 'CompareTrackedAds'
  | 'CompareRenderedImpressions'
  | 'CompareRenderRate'
  | 'CompareIVTAds'
  | 'CompareIVTRate'
  | 'CompareEligibleAds'
  | 'CompareMeasuredAds'
  | 'CompareMeasuredRate'
  | 'CompareViewableImpressions'
  | 'CompareViewabilityRate'
  | 'CompareAdBlockCount'
  | 'CompareAdBlockRate'
  | 'CompareSuspiciousImps'
  | 'CompareUnsuspiciousImps'
  | 'CompareUnviewableImps'
  | 'CompareBlockedImps'
  | 'ComparePassedAds'
  | 'CompareAudibleViewableCustomQ2'
  | 'CompareAudibleViewableIndStdQ4'
  | 'CompareAVOCGroupMRate'
  | 'CompareAVOCRate';

export interface IFirstAndThirdPartyDashboardCardDataDisplayItem {
  value: string;
  sign: number;
  suffix: string;
  key: FirstAndThirdPartyDashboardCardDataKeys;
  compareKey: FirstAndThirdPartyDashboardCardDataCompareKeys;
  yAxisID: string;
}

export interface IFirstAndThirdPartyDashboardCardData {
  'Tracked Ads': number;
  'Impressions Rendered': number;
  'Render Rate': number;
  'Invalid Traffic Ads': number;
  'Invalid Traffic Rate': number;
  'Eligible Ads': number;
  'Measured Ads': number;
  'Measured Rate': number;
  'Viewable Impressions': number;
  'Viewable Rate': number;
  'Blocked Ads': number;
  'Blocked Rate': number;
  'AVOC GroupM Count': number;
  'AVOC Count': number;
  'AVOC GroupM Rate': number;
  'AVOC Rate': number;
}

export interface IFirstAndThirdPartyDashboardCardDataDisplay {
  'Tracked Ads': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Impressions Rendered': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Render Rate': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Invalid Traffic Ads': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Invalid Traffic Rate': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Eligible Ads': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Measured Ads': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Measured Rate': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Viewable Impressions': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Viewable Rate': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Blocked Ads': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'Blocked Rate': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'AVOC GroupM Count': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'AVOC Count': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'AVOC GroupM Rate': IFirstAndThirdPartyDashboardCardDataDisplayItem;
  'AVOC Rate': IFirstAndThirdPartyDashboardCardDataDisplayItem;
}

const firstAndThirdPartyDashboardCardDropdownList = [
  dashboardTitleTrackedAds,
  dashboardTitleImpressionsRendered,
  dashboardTitleRenderRate,
  dashboardTitleInvalidTrafficAds,
  dashboardTitleInvalidTrafficRate,
  dashboardTitleEligibleAds,
  dashboardTitleMeasuredAds,
  dashboardTitleMeasuredRate,
  dashboardTitleViewableImpressions,
  dashboardTitleViewableRate,
  dashboardTitleBlockedAds,
  dashboardTitleBlockedRate,
  dashboardTitleAudibleViewableCustomQ2,
  dashboardTitleAudibleViewableIndStdQ4,
  dashboardTitleAVOCGroupMRate,
  dashboardTitleAVOCRate,
];

interface ITableDataItem {
  AdvertiserName: string;
  TrackedAds: string;
  ImpressionRendered: string;
  RenderRate: string;
  IVTRate: string;
  GIVTAds: string;
  SIVTAds: string;
}

const advertiserColumnToSQL: Record<string, string> = {
  AdvertiserName: 'tracked_ads',
  TrackedAds: 'tracked_ads',
  ImpressionRendered: 'impression_rendered',
  RenderRate: 'render_rate',
  IVTRate: 'ivt_rate',
  GIVTAds: 'givt_ads',
  SIVTAds: 'sivt_ads',
};

/** ******** Parent View Children Interfaces/Objects **********/

interface IProps extends IWithStylesProps, ISessionStateProps, IUserStateProps, IHistoryProps {
  children: React.ReactNode;
}
interface IState extends IBaseState {
  // keeps track of which party
  currentParty: FirstOrThirdParty;
  // keps track of current tab
  currentTab: number;
  // channel dropdown
  sites: ISmallSite[];
  siteList: IMenuList;
  selectedSiteIDs: string[];
  selectBySiteID: boolean;
  forceOpenChannelsList: boolean;
  // channel group dropdown
  channelGroupsList: IMenuList;
  channelGroupsListIsLoading: boolean;
  selectByChannelGroupID: boolean;
  forceOpenChannelGroupsList: boolean;
  // bidder dropdown
  bidderList: IMenuList;
  selectedBidderIDs: string[];
  bidderListIsLoading: boolean;
  selectByBidderID: boolean;
  forceOpenBiddersList: boolean;
  // bidder group dropdown
  bidderGroupsList: IMenuList;
  bidderGroupsListIsLoading: boolean;
  selectByBidderGroupID: boolean;
  forceOpenBidderGroupList: boolean;
  // device type dropdown
  deviceTypeList: IMenuList;
  selectedDeviceTypes: string[];
  forceOpenDeviceTypeList: boolean;
  selectAllDeviceTypes: boolean;
  // toggles for selecting all of dropdown type
  selectAllChannels: boolean;
  selectAllBidders: boolean;
  // calender props
  timezone: string;
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  calDisplayStartDate: moment.Moment;
  calDisplayEndDate: moment.Moment;
  compStartDate: moment.Moment;
  compEndDate: moment.Moment;
  compDisplayStartDate: moment.Moment;
  compDisplayEndDate: moment.Moment;
  compareEnabled: boolean;
  calFocusedInput?: IFocusedInput;
  compFocusedInput?: IFocusedInput;
  range1Selected: string;
  range2Selected: string;
  range1DisplaySelected: string;
  range2DisplaySelected: string;
  densityRangeAgg: DateGroup;
  densityRangeLive: number;
  // loading indicators / debounce
  loading1: LoadingState;
  loading2: LoadingState;
  // tracker for loading data
  count1: number;
  // raw data for calendar /compare calendar
  data1: IAnalytics[];
  data2: IAnalytics[];
  // list of active and inactive metrics
  selectedMetrics: ISelectedMetric[];
  // display information for agg cards
  dashboardCardDataDisplay: IFirstAndThirdPartyDashboardCardDataDisplay;
  dashboardCardDataCompareDisplay: IFirstAndThirdPartyDashboardCardDataDisplay;
  dashboardCardData: IFirstAndThirdPartyDashboardCardData;
  // snackbar
  snackbarMessage: string;
  snackbarError: boolean;
  snackbarNoData: boolean;
  // indicates that the query might be slow due to many channels, bidders, or duration
  slowLoad: boolean;
  madeFilterChange: boolean;
  advertiserTablePageSize: number;
}

const selectedMetricsColorMap = {
  0: '#4FC1B5',
  1: '#4952FF',
  2: '#EFCC57',
  3: '#273653',
  4: '#D81159',
  5: '#B55DF5',
} as Record<string, string>;

// formats data for the advertiser table at the bottom of the dashboard
const formatTableData = (data: IAnalytics[], maskUserInfo: boolean) => {
  return data.map((datum: IAnalytics) => {
    const newDatum = {} as ITableDataItem;

    // format the names
    const advertiserName = maskUserInfo
      ? `advtertiser_${datum.AdvertiserID}`
      : datum.AdvertiserName;
    newDatum.AdvertiserName = advertiserName;
    newDatum.TrackedAds = datum.TrackedAds.toLocaleString();
    newDatum.ImpressionRendered = datum.ImpressionRendered.toLocaleString();
    newDatum.RenderRate = toPercentage(datum.RenderRate);
    newDatum.IVTRate = toPercentage(datum.IVTRate);
    newDatum.GIVTAds = datum.GIVTAds.toLocaleString();
    newDatum.SIVTAds = datum.SIVTAds.toLocaleString();

    return newDatum;
  });
};

// These Custom Sort Functions DO NOT sort all the data in the table, instead it just sorts what is showing on the table
// These are needed since we turn the data into strings, but we want to sort by their actual values
// The full data sort is determined on the actual query via an order by
const customSort = (
  key:
    | 'AdvertiserName'
    | 'TrackedAds'
    | 'ImpressionRendered'
    | 'RenderRate'
    | 'IVTRate'
    | 'GIVTAds'
    | 'SIVTAds',
) => {
  if (key.indexOf('Rate') > 0) {
    return (a: ITableDataItem, b: ITableDataItem) => {
      return (
        parseFloat(a[key].slice(0, a[key].length - 1).replace(/,/g, '')) -
        parseFloat(b[key].slice(0, b[key].length - 1).replace(/,/g, ''))
      );
    };
  }
  return (a: ITableDataItem, b: ITableDataItem) => {
    return parseFloat(a[key].replace(/,/g, '')) - parseFloat(b[key].replace(/,/g, ''));
  };
};

const advertiserTableColumns = [
  {
    title: 'Advertiser Name',
    field: 'AdvertiserName',
    searchable: false,
    defaultSort: 'desc',
    sorting: false,
    headerStyle: { minWidth: 200, maxHeight: 70 },
    cellStyle: { minWidth: 200 },
  },
  {
    title: 'Tracked Ads',
    field: 'TrackedAds',
    searchable: false,
    defaultSort: 'desc',
    customSort: customSort('TrackedAds'),
    headerStyle: { minWidth: 120, maxHeight: 70 },
    cellStyle: { minWidth: 120 },
  },
  {
    title: 'Imps Rendered',
    field: 'ImpressionRendered',
    searchable: false,
    defaultSort: 'asc',
    customSort: customSort('ImpressionRendered'),
    headerStyle: { minWidth: 120, maxHeight: 70 },
    cellStyle: { minWidth: 120 },
  },
  {
    title: 'Render Rate',
    field: 'RenderRate',
    searchable: false,
    defaultSort: 'asc',
    customSort: customSort('RenderRate'),
    headerStyle: { minWidth: 120, maxHeight: 70 },
    cellStyle: { minWidth: 120 },
  },
  {
    title: 'IVT Rate',
    field: 'IVTRate',
    searchable: false,
    defaultSort: 'asc',
    customSort: customSort('IVTRate'),
    headerStyle: { minWidth: 120, maxHeight: 70 },
    cellStyle: { minWidth: 120 },
  },
  {
    title: 'GIVT Ads',
    field: 'GIVTAds',
    searchable: false,
    defaultSort: 'asc',
    customSort: customSort('GIVTAds'),
    headerStyle: { minWidth: 120, maxHeight: 70 },
    cellStyle: { minWidth: 120 },
  },
  {
    title: 'SIVT Ads',
    field: 'SIVTAds',
    searchable: false,
    defaultSort: 'asc',
    customSort: customSort('SIVTAds'),
    headerStyle: { minWidth: 120, maxHeight: 70 },
    cellStyle: { minWidth: 120 },
  },
] as Array<Column<ITableDataItem>>;

export const MeasurementHomeView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      section: {
        marginBottom: theme.spacing.unit,
      },
      alignRight,
      paper: {
        paddingLeft: theme.spacing.unit * 2,
        paddingRight: theme.spacing.unit * 2,
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit,
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      graphPaper: {
        padding: 8,
        marginBottom: 15,
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      selectedPaper: {
        ...theme.mixins.gutters(),
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      notSelectedPaper: {
        ...theme.mixins.gutters(),
      },
      dashboardCard: {
        backgroundColor: 'white',
        padding: 10,
      },
      title: {
        fontWeight: 400,
        fontSize: 12,
        color: '#292F36',
      },
      dashboardCardValue: {
        fontWeight: 600,
        textAlign: 'left',
        fontSize: 20,
        color: '#292F36',
      },
      dashboardCardComparePositive: {
        fontWeight: 400,
        textAlign: 'left',
        fontSize: 12,
        color: '#4FC2B5',
      },
      dashboardCardCompareNegative: {
        fontWeight: 400,
        textAlign: 'left',
        fontSize: 12,
        color: '#F05251',
      },
      densityRangeSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#6a686a',
        cursor: 'pointer',
      },
      densityRangeNotSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
        cursor: 'pointer',
      },
      dashboardToggleSelected: {
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
      },
      dashboardToggleNotSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
        cursor: 'pointer',
      },
      chartName: {
        fontSize: 16,
        fontWeight: 500,
      },
      redArrow: {
        transform: 'rotate(180deg)',
      },
      list: {
        marginTop: '20px',
        paddingLeft: '0',
        paddingTop: '0',
        paddingBottom: '0',
        marginBottom: '0',
        listStyle: 'none',
        position: 'unset',
      },
      appbar: {
        position: 'sticky',
        top: 0,
        marginLeft: 16,
        backgroundColor: primaryColor[0],
      },
      appbarTitle: {
        color: whiteColor,
        flexGrow: 1,
      },
      mainPanel: {
        position: 'relative',
        float: 'right',
        width: `calc(100vw - ${drawerWidth}px)`,
        backgroundColor,
      },
      content: {
        padding: '0',
        minHeight: 'calc(100vh - 64px)',
      },
      container,
      mainTab: {
        color: '#707070',
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
        pointer: 'cursor',
      },
      mainTabSelected: {
        color: '#5D66FA',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
        pointer: 'cursor',
      },
      unselectedTimeStamp: {
        '&:hover': {
          background: '#eee 0% 0% no-repeat padding-box',
          borderRadius: '5px',
          color: '#555555',
          fontSize: '12px',
        },
        color: '#555555',
        textDecoration: 'none',
        fontSize: '12px',
      },
      selectedTimeStamp: {
        background: '#eee 0% 0% no-repeat padding-box',
        color: '#292F36',
        fontSize: '12px',
        fontWeight: '700',
      },
      dashboardCardValueDisabled: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
      paperCards: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: '0px 3px 4px #E2E2E2',
        minHeight: 85,
      },
      paperCardsDisabled: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: '0px 3px 4px #E2E2E2',
        backgroundColor: '#EFEFEF',
        minHeight: 85,
      },
      dashboardCardTitleDisabled: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
      },
      dashboardCardTitleDisabledLarge: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
      root: {
        '.MuiTab-textColorInherit.Mui-selected': {
          color: 'white',
        },
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      // keeps track of which party
      currentParty:
        isFirstPartyEnabled(this.props.session.publisher) || hasRoleSuperUser(this.props.user.user)
          ? 'First Party'
          : ('Third Party' as FirstOrThirdParty),
      // keps track of current tab
      currentTab: 0,
      // channel dropdown
      sites: [] as ISmallSite[],
      siteList: {} as IMenuList,
      selectedSiteIDs: [] as string[],
      selectBySiteID: false,
      forceOpenChannelsList: false,
      // channel group dropdown
      channelGroupsList: {} as IMenuList,
      channelGroupsListIsLoading: true,
      selectByChannelGroupID: false,
      forceOpenChannelGroupsList: false,
      // bidder dropdown
      bidderList: {} as IMenuList,
      selectedBidderIDs: [] as string[],
      bidderListIsLoading: false,
      selectByBidderID: true,
      forceOpenBiddersList: false,
      // bidder group dropdown
      bidderGroupsList: {} as IMenuList,
      bidderGroupsListIsLoading: true,
      selectByBidderGroupID: false,
      forceOpenBidderGroupList: false,
      // device type dropdown
      deviceTypeList: getFirstOrThirdPartyDeviceTypesAsIMenu(
        isFirstPartyEnabled(this.props.session.publisher) || hasRoleSuperUser(this.props.user.user)
          ? 'First Party'
          : 'Third Party',
      ),
      selectedDeviceTypes: [] as string[],
      forceOpenDeviceTypeList: false,
      selectAllDeviceTypes: true,
      // toggles for selecting all of dropdown type
      selectAllChannels: true,
      selectAllBidders: true,
      // calender props
      // ias data is all EST
      timezone: 'America/New_York',
      // all days are shifted up one because there is no current day data
      calStartDate: getStartDate('America/New_York', 7, 'day').startOf('day'),
      calEndDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      calDisplayStartDate: getStartDate('America/New_York', 7, 'day').startOf('day'),
      calDisplayEndDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      compStartDate: getStartDate('America/New_York', 14, 'day').startOf('day'),
      compEndDate: getStartDate('America/New_York', 8, 'day').endOf('day'),
      compDisplayStartDate: getStartDate('America/New_York', 14, 'day').startOf('day'),
      compDisplayEndDate: getStartDate('America/New_York', 8, 'day').endOf('day'),
      compareEnabled: true,
      calFocusedInput: null,
      compFocusedInput: null,
      range1Selected: 'Last 7 Days',
      range1DisplaySelected: 'Last 7 Days',
      range2Selected: 'Previous Period',
      range2DisplaySelected: 'Compared To',
      densityRangeAgg: DateGroupDays,
      densityRangeLive: 60,
      // loading indicators / debounce
      loading1: LoadingStateLoading,
      loading2: LoadingStateLoading,
      loadingAdvertiserTable: LoadingStateLoading,
      // tracker for loading data
      count1: 0,
      // raw data for calendar /compare calendar
      data1: [] as IAnalytics[],
      data2: [] as IAnalytics[],
      // list of active and inactive metrics
      selectedMetrics: [
        { key: dashboardTitleTrackedAds, active: true } as ISelectedMetric,
        { key: dashboardTitleImpressionsRendered, active: true } as ISelectedMetric,
        { key: dashboardTitleViewableImpressions, active: true } as ISelectedMetric,
        { key: dashboardTitleViewableRate, active: false } as ISelectedMetric,
        { key: dashboardTitleInvalidTrafficAds, active: false } as ISelectedMetric,
        { key: dashboardTitleInvalidTrafficRate, active: false } as ISelectedMetric,
      ],
      // display information for agg cards
      dashboardCardDataDisplay: generateDefaultDashboardCardDisplay(),
      dashboardCardDataCompareDisplay: generateDefaultDashboardCardDisplay(),
      dashboardCardData: {
        'Tracked Ads': 0,
        'Impressions Rendered': 0,
        'Render Rate': 0,
        'Invalid Traffic Ads': 0,
        'Invalid Traffic Rate': 0,
        'Eligible Ads': 0,
        'Measured Ads': 0,
        'Measured Rate': 0,
        'Viewable Impressions': 0,
        'Viewable Rate': 0,
        'Blocked Ads': 0,
        'Blocked Rate': 0,
        'AVOC GroupM Count': 0,
        'AVOC Count': 0,
        'AVOC GroupM Rate': 0,
        'AVOC Rate': 0,
        'Valid Traffic': 0,
        'Non-Viewable Ads': 0,
        'Passed Ads': 0,
      },
      // snackbar
      snackbarMessage: '',
      snackbarError: false,
      snackbarNoData: false,
      // indicates that the query might be slow due to many channels, bidders, or duration
      slowLoad: false,
      madeFilterChange: false,
      advertiserTablePageSize: 5,
    };

    // references for the advertiser table to pass the table query
    advtertiserTableRef = React.createRef();

    // loads aggregates data
    _loadDensityData = debounce((count1: number) => {
      if (count1 !== this.state.count1) {
        return;
      }
      this.runTableQueries();
      this.runAggregateQuery(count1);
    }, 300);

    loadDensityData = (): void => {
      const count1 = this.state.count1 + 1;
      // set loading state loading so the graph won't show the axis until new data has been loaded
      this.setState(
        {
          loading1: LoadingStateLoading,
          loading2: LoadingStateLoading,
          loadingChannelTable: LoadingStateLoading,
          loadingBidderTable: LoadingStateLoading,
          count1,
        },
        () => {
          this._loadDensityData(count1);
        },
      );
    };

    runTableQueries = (): void => {
      // run the advertiser table query
      if (this.advtertiserTableRef.current !== null && this.state.currentParty === 'Third Party') {
        // @ts-ignore
        this.advtertiserTableRef.current.onQueryChange();
      }
    };

    componentDidMount() {
      this.trackEvent('Measurement Dashboard - View');
      this.preloadData();
    }

    // generates filter lists then loads graph
    preloadData = () => {
      const preloadStateObj: { [key: string]: IMenuList } = {};

      this.generateChannelList();
      this.generateBidderList();
      this.generateBidderGroupAndChannelGroupLists();

      this.setState(preloadStateObj, () => {
        // load the graph and tables
        this.loadDensityData();
      });
    };

    generateChannelList = async () => {
      const [smallSites, err] = await getSmallSites(this.props.session.publisher.ID);
      if (err) {
        this.setState({
          snackbarError: true,
          snackbarMessage: 'Error loading sites',
        });
        return;
      }

      const sites = smallSites || [];
      const siteList: IMenuList = sites.reduce((acc: IMenuList, site: ISmallSite) => {
        const siteName = checkUserInfoMasked(this.props.user.user)
          ? `demo_site_${site.ID}`
          : site.Name;
        acc[site.ID] = {
          title: siteName,
          selected: false,
          ID: site.ID.toString(),
        };
        return acc;
      }, {});
      this.setState({
        sites,
        siteList,
      });
    };

    generateBidderList = () => {
      this.setState(
        {
          bidderListIsLoading: true,
        },
        () => {
          queryHeaderBidders({
            query: {
              publisher_id: this.props.session.publisher.ID,
              publisher_ids: '',
              site_ids: this.state.selectAllChannels ? '' : this.state.selectedSiteIDs.join(','),
              include_deleted: false,
            },
          })
            .then((headerbidders: IHeaderBidder[]) => {
              // keep any previously selected bidders as selected
              const selectedBidderMap = {} as Record<string, boolean>;
              const allBidderIDs: string[] = [];
              for (const item of Object.values(this.state.bidderList)) {
                if (item.selected) {
                  selectedBidderMap[item.ID] = true;
                }
              }

              const bidderList: IMenuList = {};

              for (const bidder of headerbidders) {
                bidderList[bidder.ID] = {
                  title: checkUserInfoMasked(this.props.user.user)
                    ? `demo_bidder_${bidder.ID}`
                    : bidder.Name,
                  selected: !!selectedBidderMap[bidder.ID],
                  ID: bidder.ID,
                };
              }

              this.setState({
                allBidderIDs,
                bidderList,
                bidderListIsLoading: false,
              });
            })
            .catch(() => {
              this.setState({
                snackbarError: true,
                snackbarMessage: 'Error loading headerbidders for filter dropdown',
                bidderListIsLoading: false,
              });
            });
        },
      );
    };

    generateBidderGroupAndChannelGroupLists = () => {
      this.setState(
        {
          channelGroupsListIsLoading: true,
          bidderGroupsListIsLoading: true,
        },
        () => {
          Promise.all([
            getChannelGroupsPromise({
              publisherID: this.props.session.publisher.ID,
            }),
            getBidderGroups({
              query: {
                publisher_id: this.props.session.publisher.ID,
              },
            }),
          ])
            .then((resp: any[]) => {
              const channelGroups = resp[0].channelGroups;
              const channelGroupsList: IMenuList = {};
              channelGroups.forEach((cg: IChannelGroup) => {
                channelGroupsList[cg.ID] = {
                  title: checkUserInfoMasked(this.props.user.user)
                    ? `demo_channel_group_${cg.ID}`
                    : cg.Name,
                  selected: false,
                  ID: cg.ID.toString(),
                  siteIDs: cg.ChannelIDs.split(','),
                };
              });

              const bidderGroups = resp[1].bidderGroups;
              const bidderGroupsList: IMenuList = {};
              bidderGroups.forEach((bg: IBidderGroup) => {
                bidderGroupsList[bg.ID] = {
                  title: checkUserInfoMasked(this.props.user.user)
                    ? `demo_bidder_group_${bg.ID}`
                    : bg.Name,
                  selected: false,
                  ID: bg.ID.toString(),
                  siteIDs: bg.BidderIDs.split(','),
                };
              });

              this.setState({
                bidderGroupsList,
                channelGroupsList,
                channelGroupsListIsLoading: false,
                bidderGroupsListIsLoading: false,
              });
            })
            .catch(() => {
              this.setState({
                snackbarError: true,
                snackbarMessage: 'Error loading channel and bidder groups for filter dropdown',
                channelGroupsListIsLoading: false,
                bidderGroupsListIsLoading: false,
              });
            });
        },
      );
    };

    trackEvent = (
      eventTitle: DashboardEventTypes,
      graph_id = 0,
      start_date = '',
      end_date = '',
      comp_start_date = '',
      comp_end_date = '',
      timezone = '',
      time_to_load_sec = 0,
      query_failed = false,
      query_duration = 0,
      comp_query_duration_days = 0,
      compare_enabled = false,
    ) => {
      const is_super_user = hasRoleSuperUser(this.props.user.user);
      if (
        eventTitle === 'Measurement Dashboard - Open Calendar' ||
        eventTitle === 'Measurement Dashboard - View' ||
        eventTitle === 'Measurement Dashboard - Open Filter Box' ||
        eventTitle === 'Measurement Dashboard - Open Compare Calendar'
      ) {
        track(
          eventTitle,
          {
            is_super_user,
          },
          this.props.user.user,
        );
      } else if (eventTitle === 'Measurement Dashboard - Compare Enabled') {
        track(
          eventTitle,
          {
            is_super_user,
            compare_enabled: this.state.compareEnabled,
          },
          this.props.user.user,
        );
      } else if (eventTitle === 'Measurement Dashboard - Run Agg Query') {
        track(
          eventTitle,
          {
            is_super_user,
            graph_id,
            start_date,
            end_date,
            comp_start_date,
            comp_end_date,
            timezone,
            time_to_load_sec,
            query_failed,
            query_duration_days: query_duration,
            comp_query_duration_days,
            compare_enabled,
          },
          this.props.user.user,
        );
      }
    };

    // will run a parallel request if compareEnabled for the compare calendar
    runAggregateQuery = (count1: number): void => {
      const {
        calStartDate,
        calEndDate,
        compStartDate,
        compEndDate,
        densityRangeAgg,
        compareEnabled,
        currentTab,
        currentParty,
        timezone,
      } = this.state;
      // query 1 (normal calendar)
      const start_date = formatDateStartDay(calStartDate);
      const end_date =
        this.generateEndDateForSnowflakeQuery(calEndDate).format('YYYY-MM-DDTHH:mm:ss');
      const query_duration_days = calEndDate.diff(calStartDate, 'days') + 1;
      // query 2 (compare calendar)
      const comp_start_date = formatDateStartDay(compStartDate);
      const comp_end_date =
        this.generateEndDateForSnowflakeQuery(compEndDate).format('YYYY-MM-DDTHH:mm:ss');
      const comp_query_duration_days = compEndDate.diff(compStartDate, 'days') + 1;
      // query params
      const graph_id = currentParty === 'First Party' ? 'graph6.sql' : 'graph5.sql';
      const grouping = densityRangeAgg;
      const body1 = {
        GraphID: graph_id,
        StartDate: start_date,
        EndDate: end_date,
        Timezone: timezone,
        GroupingNumber: getDateGroupNumber(grouping),
        DeviceTypes: this.getDeviceTypesForQuery(),
        TeamID: '15661',
        AnIDs: [+this.props.session.publisher.IASAnID],
      } as IIASFirstAndThirdPartyRequestBody;
      const body2 = {
        GraphID: graph_id,
        StartDate: comp_start_date,
        EndDate: comp_end_date,
        Timezone: timezone,
        GroupingNumber: getDateGroupNumber(grouping),
        DeviceTypes: this.getDeviceTypesForQuery(),
        TeamID: '15661',
        AnIDs: [+this.props.session.publisher.IASAnID],
      } as IIASFirstAndThirdPartyRequestBody;
      if (currentParty === 'Third Party') {
        body1.PublicaChannelIDs = this.getSiteIDsForQuery();
        body1.PublicaBidderIDs = this.getBidderIDsForQuery();
        body2.PublicaChannelIDs = this.getSiteIDsForQuery();
        body2.PublicaBidderIDs = this.getBidderIDsForQuery();
      }
      /** * Main Graph Queries ***/
      const queries = [
        queryFirstAndThirdPartyIasData(body1, { publisher_id: this.props.session.publisher.ID }),
        queryFirstAndThirdPartyIasData(body2, { publisher_id: this.props.session.publisher.ID }),
      ];

      // Runs the queries together in Promise All
      const queryStartTime = moment().unix();
      Promise.all(queries)
        .then((data: any[][]) => {
          const queryTotalTime = moment().unix() - queryStartTime;
          let queryFailed = false;

          // if the call is successful but there is no data
          if (!data[0][0].rows) {
            queryFailed = true;
            this.setState({
              loading1: LoadingStateNoData,
              loading2: LoadingStateNoData,
              snackbarMessage: `We are not tracking any measurement data here...\nPlease adjust filters / date range combination to match ${
                currentTab === 0
                  ? 'where you enabled IAS Measurement.'
                  : 'where you enabled IAS Third Party Measurement.\nReach out to your technical account manager for more information.'
              }`,
              snackbarNoData: true,
            });
            return;
          }

          const data1 = [] as any;
          data1.push(data[0][0].rows);
          data1.push(data[1][0].rows);

          // check the data to make sure it's there, otherwise show an error msg
          if (!data1[0].rows) {
            if (data1[0].length === 0) {
              queryFailed = true;
              if (
                !compareEnabled &&
                formatDateStartDay(this.state.calEndDate) ===
                  formatDateStartDay(this.state.calStartDate)
              ) {
                this.setState({
                  loading1: LoadingStateNoData,
                  loading2: LoadingStateNoData,
                });
              } else {
                this.setState({
                  loading1: LoadingStateNoData,
                  loading2: LoadingStateNoData,
                });
              }
              // if there is no compare data (query went wrong or publisher is new), reload the graph without compare
            } else if (compareEnabled && (!data1[1] || (data1[1] && data1[1].length === 0))) {
              queryFailed = true;
              this.setState(
                {
                  loading1: LoadingStateNoData,
                  loading2: LoadingStateNoData,
                  snackbarError: false,
                  snackbarMessage:
                    'There is no compare data available yet for this period. The dashboard will reload without compare mode enabled.',
                  compareEnabled: false,
                },
                () => this.runAggregateQuery(count1),
              );
              // check max date queries, if they didn't return a date, try turning off mergeLiveData and running again
            }
          }
          if (queryFailed) {
            // track events for mixpanel
            this.trackEvent(
              'Measurement Dashboard - Run Agg Query',
              this.state.currentParty === 'First Party' ? 6 : 5,
              start_date,
              end_date,
              compareEnabled ? comp_start_date : '',
              compareEnabled ? comp_end_date : '',
              timezone,
              queryTotalTime,
              true,
              query_duration_days,
              compareEnabled ? comp_query_duration_days : 0,
              compareEnabled,
            );
            return;
          }
          if (count1 !== this.state.count1) {
            return;
          }

          // track events for mixpanel
          this.trackEvent(
            'Measurement Dashboard - Run Agg Query',
            this.state.currentParty === 'First Party' ? 6 : 5,
            start_date,
            end_date,
            compareEnabled ? comp_start_date : '',
            compareEnabled ? comp_end_date : '',
            timezone,
            queryTotalTime,
            false,
            query_duration_days,
            compareEnabled ? comp_query_duration_days : 0,
            compareEnabled,
          );
          const [dashboardCardData, dashboardCardDataDisplay] = this.createDashboardCardDisplay(
            data1[0],
            false,
          );
          this.setState(
            {
              loading1: LoadingStateHasData,
              loading2: LoadingStateHasData,
              data1: data1[0] ? data1[0] : [], // null check for ele in data1
              data2: data1[1] ? data1[1] : [], // another null check
              dashboardCardDataDisplay,
              dashboardCardData,
            },
            () => {
              if (compareEnabled) {
                const dashboardCardDataCompareDisplay = this.createDashboardCardDisplay(
                  data1[1],
                  true,
                )[1];
                this.setState({
                  dashboardCardDataCompareDisplay,
                });
              }
            },
          );
        })
        .catch(() => {
          // track events for mixpanel
          const queryTotalTime = moment().unix() - queryStartTime;
          this.trackEvent(
            'Measurement Dashboard - Run Agg Query',
            this.state.currentParty === 'First Party' ? 6 : 5,
            start_date,
            end_date,
            compareEnabled ? comp_start_date : '',
            compareEnabled ? comp_end_date : '',
            timezone,
            queryTotalTime,
            true,
            query_duration_days,
            compareEnabled ? comp_query_duration_days : 0,
            compareEnabled,
          );
          this.setState({
            loading1: LoadingStateNoData,
            loading2: LoadingStateNoData,
            snackbarMessage: 'Something went wrong: Please try again.',
            snackbarError: true,
          });
        });
    };

    // creates dashboard card display data from query results
    createDashboardCardDisplay = (
      data1Agg: IIASFirstAndThirdPartyAnalytics[],
      isCompareCalendar: boolean,
    ): [IFirstAndThirdPartyDashboardCardData, IFirstAndThirdPartyDashboardCardDataDisplay] => {
      // aggregate all data from live or agg
      let trackedAds = 0,
        impressionsRendered = 0,
        renderRate = 0,
        invalidTrafficAds = 0,
        invalidTrafficRate = 0,
        eligibleAds = 0,
        measuredAds = 0,
        measuredRate = 0,
        viewableImpressions = 0,
        viewableRate = 0,
        blockedAds = 0,
        blockedRate = 0,
        grossImps = 0,
        measuredRenderedImps = 0,
        measuredGivtRenderedImps = 0,
        measuredSivtRenderedImps = 0,
        renderedImps = 0,
        givtImps = 0,
        givtRenderedImps = 0,
        sivtRenderedImps = 0,
        inViewGivtRenderedImps = 0,
        inViewRenderedImps = 0,
        blockedImps = 0,
        mobileGrossImps = 0,
        measuredImps = 0,
        sivtImps = 0,
        inViewSivtRenderedImps = 0,
        totalImps = 0,
        suspiciousImps = 0,
        mobileTotalImps = 0,
        mobileRenderedImps = 0,
        unmeasuredImps = 0,
        AudibleViewableCustomQ2 = 0,
        AudibleViewableIndStdQ4 = 0,
        AVOCGroupMRate = 0,
        AVOCRate = 0;

      data1Agg.forEach((datum: IIASFirstAndThirdPartyAnalytics) => {
        // metrics
        trackedAds += isNaN(datum.TrackedAds) ? 0 : datum.TrackedAds;
        impressionsRendered += isNaN(datum.RenderedImpressions) ? 0 : datum.RenderedImpressions;
        invalidTrafficAds += isNaN(datum.IVTAds) ? 0 : datum.IVTAds;
        eligibleAds += isNaN(datum.EligibleAds) ? 0 : datum.EligibleAds;
        measuredAds += isNaN(datum.MeasuredAds) ? 0 : datum.MeasuredAds;
        viewableImpressions += isNaN(datum.ViewableImpressions) ? 0 : datum.ViewableImpressions;
        blockedAds += isNaN(datum.AdBlockCount) ? 0 : datum.AdBlockCount;
        AudibleViewableCustomQ2 += isNaN(datum.AudibleViewableCustomQ2)
          ? 0
          : datum.AudibleViewableCustomQ2;
        AudibleViewableIndStdQ4 += isNaN(datum.AudibleViewableIndStdQ4)
          ? 0
          : datum.AudibleViewableIndStdQ4;
        AVOCGroupMRate += isNaN(datum.AVOCGroupMRate) ? 0 : datum.AVOCGroupMRate;
        AVOCRate += isNaN(datum.AVOCRate) ? 0 : datum.AVOCRate;

        // metrics for rate calculation
        grossImps += isNaN(datum.GrossImps) ? 0 : datum.GrossImps;
        mobileGrossImps += isNaN(datum.MobileGrossImps) ? 0 : datum.MobileGrossImps;
        measuredRenderedImps += isNaN(datum.MeasuredRenderedImps) ? 0 : datum.MeasuredRenderedImps;
        measuredGivtRenderedImps += isNaN(datum.MeasuredGivtRenderedImps)
          ? 0
          : datum.MeasuredGivtRenderedImps;
        measuredSivtRenderedImps += isNaN(datum.MeasuredSivtRenderedImps)
          ? 0
          : datum.MeasuredSivtRenderedImps;
        renderedImps += isNaN(datum.RenderedImpressions) ? 0 : datum.RenderedImpressions;
        measuredImps += isNaN(datum.MeasuredImps) ? 0 : datum.MeasuredImps;
        unmeasuredImps += isNaN(datum.UnmeasuredImps) ? 0 : datum.UnmeasuredImps;
        givtImps += isNaN(datum.GivtImps) ? 0 : datum.GivtImps;
        sivtImps += isNaN(datum.SivtImps) ? 0 : datum.SivtImps;
        givtRenderedImps += isNaN(datum.GivtRenderedImps) ? 0 : datum.GivtRenderedImps;
        sivtRenderedImps += isNaN(datum.SivtRenderedImps) ? 0 : datum.SivtRenderedImps;
        inViewGivtRenderedImps += isNaN(datum.InViewGivtRenderedImps)
          ? 0
          : datum.InViewGivtRenderedImps;
        inViewSivtRenderedImps += isNaN(datum.InViewSivtRenderedImps)
          ? 0
          : datum.InViewSivtRenderedImps;
        inViewRenderedImps += isNaN(datum.InViewRenderedImps) ? 0 : datum.InViewRenderedImps;
        blockedImps += isNaN(datum.BlockedImps) ? 0 : datum.BlockedImps;
        totalImps += isNaN(datum.TotalImps) ? 0 : datum.TotalImps;
        suspiciousImps += isNaN(datum.SuspiciousImps) ? 0 : datum.SuspiciousImps;
        mobileTotalImps += isNaN(datum.MobileTotalImps) ? 0 : datum.MobileTotalImps;
        mobileRenderedImps += isNaN(datum.MobileRenderedImps) ? 0 : datum.MobileRenderedImps;
      });

      // rate calculations
      if (this.state.currentParty === 'Third Party') {
        renderRate +=
          (renderedImps - givtRenderedImps - sivtRenderedImps) /
          (measuredRenderedImps - measuredGivtRenderedImps - measuredSivtRenderedImps);
        invalidTrafficRate += (givtRenderedImps + sivtRenderedImps) / grossImps;
        measuredRate +=
          (measuredRenderedImps - measuredGivtRenderedImps - measuredSivtRenderedImps) /
          (grossImps - givtImps - sivtImps);
        viewableRate +=
          (inViewRenderedImps - inViewGivtRenderedImps - inViewSivtRenderedImps) /
          (measuredRenderedImps - measuredGivtRenderedImps - measuredSivtRenderedImps);
        blockedRate += blockedImps / mobileGrossImps;
        AVOCGroupMRate += AudibleViewableCustomQ2 / renderedImps;
        AVOCRate += AudibleViewableIndStdQ4 / renderedImps;
      } else if (this.state.currentParty === 'First Party') {
        renderRate += mobileRenderedImps / mobileTotalImps;
        invalidTrafficRate += suspiciousImps / totalImps;
        measuredRate += measuredImps / (measuredImps + unmeasuredImps);
        viewableRate += viewableImpressions / measuredImps;
        blockedRate += blockedAds / mobileTotalImps;
        AVOCGroupMRate += AudibleViewableCustomQ2 / renderedImps;
        AVOCRate += AudibleViewableIndStdQ4 / renderedImps;
      }

      let dashboardCardData = {
        'Tracked Ads': trackedAds,
        'Impressions Rendered': impressionsRendered,
        'Render Rate': renderRate,
        'Invalid Traffic Ads': invalidTrafficAds,
        'Invalid Traffic Rate': invalidTrafficRate,
        'Eligible Ads': eligibleAds,
        'Measured Ads': measuredAds,
        'Measured Rate': measuredRate,
        'Viewable Impressions': viewableImpressions,
        'Viewable Rate': viewableRate,
        'Blocked Ads': blockedAds,
        'Blocked Rate': blockedRate,
        'AVOC GroupM Count': AudibleViewableCustomQ2,
        'AVOC Count': AudibleViewableIndStdQ4,
        'AVOC GroupM Rate': AVOCGroupMRate,
        'AVOC Rate': AVOCRate,
      } as Record<FirstAndThirdPartyDashboardCardDataTitles, number>;

      if (isCompareCalendar) {
        dashboardCardData = this.state.dashboardCardData;
      }

      // create strings for display
      // sums
      const trackedAdsString = formatHugeNumberToShortenedString(trackedAds);
      const impressionsRenderedString = formatHugeNumberToShortenedString(impressionsRendered);
      const invalidTrafficAdsString = formatHugeNumberToShortenedString(invalidTrafficAds);
      const eligibleAdsString = formatHugeNumberToShortenedString(eligibleAds);
      const measuredAdsString = formatHugeNumberToShortenedString(measuredAds);
      const viewableImpressionsString = formatHugeNumberToShortenedString(viewableImpressions);
      const blockedAdsString = formatHugeNumberToShortenedString(blockedAds);
      const audibleViewableCustomQ2String =
        formatHugeNumberToShortenedString(AudibleViewableCustomQ2);
      const audibleViewableIndStdQ4String =
        formatHugeNumberToShortenedString(AudibleViewableIndStdQ4);
      // rates
      const renderRateString = formatRateToTwoDecimals(renderRate);
      const invalidTrafficRateString = formatRateToTwoDecimals(invalidTrafficRate);
      const measuredRateString = formatRateToTwoDecimals(measuredRate);
      const viewableRateString = formatRateToTwoDecimals(viewableRate);
      const blockedRateString = formatRateToTwoDecimals(blockedRate);
      const AVOCGroupMRateString = formatRateToTwoDecimals(AVOCGroupMRate);
      const AVOCRateString = formatRateToTwoDecimals(AVOCRate);

      // this function runs twice, once for the original data and once for compare
      // the compare data needs the results of the original to calculate the % difference
      const dashboardCardDataDisplay: IFirstAndThirdPartyDashboardCardDataDisplay =
        !isCompareCalendar
          ? {
              // sums
              'Tracked Ads': {
                value: trackedAdsString,
                sign: 1,
                key: dashboardKeyTrackedAds,
                compareKey: dashboardCompareKeyTrackedAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'Impressions Rendered': {
                value: impressionsRenderedString,
                sign: 1,
                key: dashboardKeyImpressionsRendered,
                compareKey: dashboardCompareKeyImpressionsRendered,
                yAxisID: 'huge',
                suffix: '',
              },
              'Eligible Ads': {
                value: eligibleAdsString,
                sign: 1,
                key: dashboardKeyEligibleAds,
                compareKey: dashboardCompareKeyEligibleAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'Measured Ads': {
                value: measuredAdsString,
                sign: 1,
                key: dashboardKeyMeasuredAds,
                compareKey: dashboardCompareKeyMeasuredAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'Viewable Impressions': {
                value: viewableImpressionsString,
                sign: 1,
                key: dashboardKeyViewableImpressions,
                compareKey: dashboardCompareKeyViewableImpressions,
                yAxisID: 'huge',
                suffix: '',
              },
              'Invalid Traffic Ads': {
                value: invalidTrafficAdsString,
                sign: 1,
                key: dashboardKeyInvalidTrafficAds,
                compareKey: dashboardCompareKeyInvalidTrafficAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'Blocked Ads': {
                value: blockedAdsString,
                sign: 1,
                key: dashboardKeyBlockedAds,
                compareKey: dashboardCompareKeyBlockedAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'AVOC GroupM Count': {
                value: audibleViewableCustomQ2String,
                sign: 1,
                key: dashboardKeyAudibleViewableCustomQ2,
                compareKey: dashboardCompareKeyAudibleViewableCustomQ2,
                yAxisID: 'huge',
                suffix: '',
              },
              'AVOC Count': {
                value: audibleViewableIndStdQ4String,
                sign: 1,
                key: dashboardKeyAudibleViewableIndStdQ4,
                compareKey: dashboardCompareKeyAudibleViewableIndStdQ4,
                yAxisID: 'huge',
                suffix: '',
              },
              // rates
              'Measured Rate': {
                value: measuredRateString,
                sign: 1,
                key: dashboardKeyMeasuredRate,
                compareKey: dashboardCompareKeyMeasuredRate,
                yAxisID: 'small',
                suffix: '',
              },
              'Render Rate': {
                value: renderRateString,
                sign: 1,
                key: dashboardKeyRenderRate,
                compareKey: dashboardCompareKeyRenderRate,
                yAxisID: 'small',
                suffix: '',
              },
              'Viewable Rate': {
                value: viewableRateString,
                sign: 1,
                key: dashboardKeyViewableRate,
                compareKey: dashboardCompareKeyViewableRate,
                yAxisID: 'small',
                suffix: '',
              },
              'Invalid Traffic Rate': {
                value: invalidTrafficRateString,
                sign: 1,
                key: dashboardKeyInvalidTrafficRate,
                compareKey: dashboardCompareKeyInvalidTrafficRate,
                yAxisID: 'small',
                suffix: '',
              },
              'Blocked Rate': {
                value: blockedRateString,
                sign: 1,
                key: dashboardKeyBlockedRate,
                compareKey: dashboardCompareKeyBlockedRate,
                yAxisID: 'small',
                suffix: '',
              },
              'AVOC GroupM Rate': {
                value: AVOCGroupMRateString,
                sign: 1,
                key: dashboardKeyAVOCGroupMRate,
                compareKey: dashboardCompareKeyAVOCGroupMRate,
                yAxisID: 'small',
                suffix: '',
              },
              'AVOC Rate': {
                value: AVOCRateString,
                sign: 1,
                key: dashboardKeyAVOCRate,
                compareKey: dashboardCompareKeyAVOCRate,
                yAxisID: 'small',
                suffix: '',
              },
            }
          : {
              // sums
              'Tracked Ads': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleTrackedAds] || 0,
                  trackedAds,
                ),
                sign: Math.sign((dashboardCardData[dashboardTitleTrackedAds] || 0) - trackedAds),
                key: dashboardKeyTrackedAds,
                compareKey: dashboardCompareKeyTrackedAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'Impressions Rendered': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleImpressionsRendered] || 0,
                  impressionsRendered,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleImpressionsRendered] || 0) - impressionsRendered,
                ),
                key: dashboardKeyImpressionsRendered,
                compareKey: dashboardCompareKeyImpressionsRendered,
                yAxisID: 'huge',
                suffix: '',
              },
              'Eligible Ads': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleEligibleAds] || 0,
                  eligibleAds,
                ),
                sign: Math.sign((dashboardCardData[dashboardTitleEligibleAds] || 0) - eligibleAds),
                key: dashboardKeyEligibleAds,
                compareKey: dashboardCompareKeyEligibleAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'Measured Ads': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleMeasuredAds] || 0,
                  measuredAds,
                ),
                sign: Math.sign((dashboardCardData[dashboardTitleMeasuredAds] || 0) - measuredAds),
                key: dashboardKeyMeasuredAds,
                compareKey: dashboardCompareKeyMeasuredAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'Viewable Impressions': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleViewableImpressions] || 0,
                  viewableImpressions,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleViewableImpressions] || 0) - viewableImpressions,
                ),
                key: dashboardKeyViewableImpressions,
                compareKey: dashboardCompareKeyViewableImpressions,
                yAxisID: 'huge',
                suffix: '',
              },
              'Invalid Traffic Ads': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleInvalidTrafficAds] || 0,
                  invalidTrafficAds,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleInvalidTrafficAds] || 0) - invalidTrafficAds,
                ),
                key: dashboardKeyInvalidTrafficAds,
                compareKey: dashboardCompareKeyInvalidTrafficAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'Blocked Ads': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleBlockedAds] || 0,
                  blockedAds,
                ),
                sign: Math.sign((dashboardCardData[dashboardTitleBlockedAds] || 0) - blockedAds),
                key: dashboardKeyBlockedAds,
                compareKey: dashboardCompareKeyBlockedAds,
                yAxisID: 'huge',
                suffix: '',
              },
              'AVOC GroupM Count': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleAudibleViewableCustomQ2] || 0,
                  AudibleViewableCustomQ2,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleAudibleViewableCustomQ2] || 0) -
                    AudibleViewableCustomQ2,
                ),
                key: dashboardKeyAudibleViewableCustomQ2,
                compareKey: dashboardCompareKeyAudibleViewableCustomQ2,
                yAxisID: 'huge',
                suffix: '',
              },
              'AVOC Count': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleAudibleViewableIndStdQ4] || 0,
                  AudibleViewableIndStdQ4,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleAudibleViewableIndStdQ4] || 0) -
                    AudibleViewableIndStdQ4,
                ),
                key: dashboardKeyAudibleViewableIndStdQ4,
                compareKey: dashboardCompareKeyAudibleViewableIndStdQ4,
                yAxisID: 'huge',
                suffix: '',
              },
              // rates
              'Measured Rate': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleMeasuredRate] || 0,
                  measuredRate,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleMeasuredRate] || 0) - measuredRate,
                ),
                key: dashboardKeyMeasuredRate,
                compareKey: dashboardCompareKeyMeasuredRate,
                yAxisID: 'small',
                suffix: '',
              },
              'Render Rate': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleRenderRate] || 0,
                  renderRate,
                ),
                sign: Math.sign((dashboardCardData[dashboardTitleRenderRate] || 0) - renderRate),
                key: dashboardKeyRenderRate,
                compareKey: dashboardCompareKeyRenderRate,
                yAxisID: 'small',
                suffix: '',
              },
              'Viewable Rate': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleViewableRate] || 0,
                  viewableRate,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleViewableRate] || 0) - viewableRate,
                ),
                key: dashboardKeyViewableRate,
                compareKey: dashboardCompareKeyViewableRate,
                yAxisID: 'small',
                suffix: '',
              },
              'Invalid Traffic Rate': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleInvalidTrafficRate] || 0,
                  invalidTrafficRate,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleInvalidTrafficRate] || 0) - invalidTrafficRate,
                ),
                key: dashboardKeyInvalidTrafficRate,
                compareKey: dashboardCompareKeyInvalidTrafficRate,
                yAxisID: 'small',
                suffix: '',
              },
              'Blocked Rate': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleBlockedRate] || 0,
                  blockedRate,
                ),
                sign: Math.sign((dashboardCardData[dashboardTitleBlockedRate] || 0) - blockedRate),
                key: dashboardKeyBlockedRate,
                compareKey: dashboardCompareKeyBlockedRate,
                yAxisID: 'small',
                suffix: '',
              },
              'AVOC GroupM Rate': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleAVOCGroupMRate] || 0,
                  AVOCGroupMRate,
                ),
                sign: Math.sign(
                  (dashboardCardData[dashboardTitleAVOCGroupMRate] || 0) - AVOCGroupMRate,
                ),
                key: dashboardKeyAVOCGroupMRate,
                compareKey: dashboardCompareKeyAVOCGroupMRate,
                yAxisID: 'small',
                suffix: '',
              },
              'AVOC Rate': {
                value: calculatePercentageChange(
                  dashboardCardData[dashboardTitleAVOCRate] || 0,
                  AVOCRate,
                ),
                sign: Math.sign((dashboardCardData[dashboardTitleAVOCRate] || 0) - AVOCRate),
                key: dashboardKeyAVOCRate,
                compareKey: dashboardCompareKeyAVOCRate,
                yAxisID: 'small',
                suffix: '',
              },
            };

      return [dashboardCardData, dashboardCardDataDisplay];
    };

    handleSwitchActiveMetric = (idx: number): void => {
      const selectedMetrics = [...this.state.selectedMetrics];
      selectedMetrics[idx].active = !selectedMetrics[idx].active;

      this.setState({
        selectedMetrics,
      });
    };

    handleSwitchCardSelected = (
      newkey: FirstAndThirdPartyDashboardCardDataTitles,
      index: number,
    ): void => {
      // swap out metric info for selected metrics
      const selectedMetrics = [...this.state.selectedMetrics];
      selectedMetrics[index] = { key: newkey, active: true };

      this.setState({
        selectedMetrics,
      });
    };

    handleCompareEnabled = () => {
      this.setState(
        (prevState) => {
          return { compareEnabled: !prevState.compareEnabled };
        },
        () => this.trackEvent('Measurement Dashboard - Compare Enabled'),
      );
    };

    // sets calendar start and end dates when focus changes between compare / normal calendar
    handleCalendarFocusedInputChange = (
      isCompareCalendar: boolean,
    ): ((focusedInput: IFocusedInput) => void) => {
      if (isCompareCalendar) {
        return (focusedInput: IFocusedInput) => {
          if (focusedInput === null) {
            this.setState({
              calFocusedInput: null,
              compFocusedInput: null,
              calDisplayStartDate: this.state.calStartDate,
              calDisplayEndDate: this.state.calEndDate,
              compDisplayStartDate: this.state.compStartDate,
              compDisplayEndDate: this.state.compEndDate,
            });
          } else {
            this.setState({
              calFocusedInput: null,
              compFocusedInput: focusedInput,
            });
          }
        };
      } else {
        return (focusedInput: IFocusedInput) => {
          if (focusedInput === null) {
            this.setState({
              calFocusedInput: null,
              compFocusedInput: null,
              calDisplayStartDate: this.state.calStartDate,
              calDisplayEndDate: this.state.calEndDate,
              compDisplayStartDate: this.state.compStartDate,
              compDisplayEndDate: this.state.compEndDate,
            });
          } else {
            this.setState({
              calFocusedInput: focusedInput,
              compFocusedInput: null,
            });
          }
        };
      }
    };

    handleCalendarDateChange = (
      isCompareCalendar: boolean,
    ): ((start: moment.Moment, end: moment.Moment, isCustom: boolean) => void) => {
      if (isCompareCalendar) {
        return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
          if (isCustom) {
            this.handleChangeAggRangeSelected('Custom', true, true);
          }
          this.setState({
            compDisplayEndDate: end,
            compDisplayStartDate: start,
          });
        };
      } else {
        return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
          if (isCustom) {
            this.handleChangeAggRangeSelected('Custom', false, true);
          }
          this.setState({
            calDisplayEndDate: end,
            calDisplayStartDate: start,
          });
        };
      }
    };

    handleDateRangeChange = (
      start: moment.Moment,
      end: moment.Moment,
      isCompareCalendar: boolean,
    ): void => {
      const startDate = cloneDeep(start).startOf('day');
      const endDate = cloneDeep(end).endOf('day');

      if (isCompareCalendar) {
        this.setState(
          {
            compStartDate: startDate,
            compEndDate: endDate,
            densityRangeAgg: getValidDateGroup(this.state.densityRangeAgg, start, end),
            page: 0,
          },
          () => {
            this.loadDensityData();
          },
        );
      } else {
        this.setState(
          {
            calStartDate: startDate,
            calEndDate: endDate,
            densityRangeAgg: getValidDateGroup(this.state.densityRangeAgg, start, end),
          },
          () => {
            const compStartDate = this.findCompareStartDate(this.state.timezone);
            const compEndDate = this.findCompareEndDate(this.state.timezone);
            this.setState(
              {
                compDisplayStartDate: compStartDate,
                compDisplayEndDate: compEndDate,
                compStartDate,
                compEndDate,
                page: 0,
              },
              () => {
                this.loadDensityData();
              },
            );
          },
        );
      }
    };

    // changes the range selected (last month, last week, etc) and displayed text
    handleChangeAggRangeSelected = (
      rangeSelected: string,
      isCompareCalendar: boolean,
      applied?: boolean,
    ): void => {
      if (isCompareCalendar) {
        if (applied) {
          this.setState({
            range2Selected: rangeSelected,
            range2DisplaySelected: rangeSelected,
          });
        } else {
          this.setState({ range2DisplaySelected: rangeSelected });
        }
      } else {
        if (applied) {
          this.setState({
            range1Selected: rangeSelected,
            range1DisplaySelected: rangeSelected,
          });
        } else {
          this.setState({ range1DisplaySelected: rangeSelected });
        }
      }
    };

    generateEndDateForSnowflakeQuery = (endDate: moment.Moment): moment.Moment => {
      return endDate;
    };

    handleChannelAcceptClick = (values: IMenuList): void => {
      const siteList = cloneDeep(this.state.siteList);
      const originalValues = Object.values(siteList);
      const isWholeList = Object.values(values).length === originalValues.length;
      const selectedSiteIDs = Object.values(values)
        .filter((item: IListItem) => item.selected)
        .map((item: IListItem) => item.ID)
        .sort();

      // if all sites are selected, we switch back to selectAllChannels and unselect all sites
      const allSelected =
        selectedSiteIDs.length === originalValues.length || selectedSiteIDs.length === 0;
      if (allSelected) {
        this.setState({
          selectedSiteIDs,
          selectAllChannels: true,
          selectBySiteID: false,
          forceOpenChannelsList: false,
          madeFilterChange: true,
        });
        return;
      }

      // if no changes, just return
      if (selectedSiteIDs.join(',') === this.state.selectedSiteIDs.join(',')) {
        return;
      }

      if (allSelected) {
        for (const item of Object.values(siteList)) {
          item.selected = false;
        }
      }

      // if all or no sites are selected, switch back to select all sites
      this.setState(
        {
          selectedSiteIDs: allSelected ? [] : selectedSiteIDs,
          selectAllChannels: allSelected || selectedSiteIDs.length === 0,
          siteList: allSelected ? siteList : isWholeList ? values : this.state.siteList,
          madeFilterChange: true,
          forceOpenChannelsList: false,
          selectBySiteID: selectedSiteIDs.length !== 0,
          selectByChannelGroupID: false,
        },
        () => {
          this.generateBidderList();
        },
      );
    };

    handleChannelGroupsAcceptClick = (values: IMenuList): void => {
      const channelGroupsList = cloneDeep(this.state.channelGroupsList);
      const selectedSiteIDs = [] as string[];
      for (const item of Object.values(values).filter((item: IListItem) => item.selected)) {
        if (item.siteIDs) {
          selectedSiteIDs.push(...item.siteIDs);
        }
      }

      this.setState(
        {
          selectedSiteIDs: selectedSiteIDs.sort(),
          channelGroupsList,
          madeFilterChange: true,
          forceOpenChannelGroupsList: false,
          selectAllChannels: selectedSiteIDs.length === 0,
          selectByChannelGroupID: selectedSiteIDs.length !== 0,
          selectBySiteID: false,
        },
        () => {
          this.generateBidderList();
        },
      );
    };

    handleBidderAcceptClick = (selectedValues: IMenuList): void => {
      const bidderList = cloneDeep(this.state.bidderList);
      const originalValues = Object.values(bidderList);
      const selectedBidderIDs = Object.values(selectedValues)
        .filter((item: IListItem) => item.selected)
        .map((item: IListItem) => item.ID)
        .sort();

      const allSelected =
        selectedBidderIDs.length === originalValues.length || selectedBidderIDs.length === 0;
      if (allSelected) {
        this.setState({
          selectedBidderIDs,
          selectAllBidders: true,
          forceOpenBiddersList: false,
          madeFilterChange: true,
        });
        return;
      }

      // if no changes, return
      if (selectedBidderIDs.join(',') === this.state.selectedBidderIDs.join(',')) {
        return;
      }

      // if all bidders are selected, we switch back to selectAllBidders and unselect all bidders
      if (allSelected) {
        for (const item of Object.values(bidderList)) {
          item.selected = false;
        }
      }

      // if all bidder ids are selected, the selected bidder ids can be blank since select all bidders will be true
      // if all or no bidders are selected, switch back to select all bidders
      this.setState({
        bidderList: allSelected ? bidderList : this.state.bidderList,
        selectedBidderIDs: allSelected ? [] : selectedBidderIDs,
        selectAllBidders: allSelected || selectedBidderIDs.length === 0,
        madeFilterChange: true,
        forceOpenBiddersList: false,
      });
    };

    handleDeviceTypeAcceptClick = (selectedValues: IMenuList): void => {
      const deviceTypeList = cloneDeep(this.state.deviceTypeList);
      const originalValues = Object.values(deviceTypeList);
      const isWholeList = Object.values(selectedValues).length === originalValues.length;
      const selectedDeviceTypes = Object.values(selectedValues)
        .filter((item: IListItem) => item.selected)
        .map((item: IListItem) => item.ID)
        .sort();

      // if all device type are selected, we switch back to selectAllDeviceType and unselect all device types
      const allSelected =
        selectedDeviceTypes.length === originalValues.length || selectedDeviceTypes.length === 0;
      if (allSelected) {
        this.setState({
          selectedDeviceTypes,
          selectAllDeviceTypes: true,
          forceOpenDeviceTypeList: false,
          madeFilterChange: true,
        });
        return;
      }

      // if no changes, return
      if (selectedDeviceTypes.join(',') === this.state.selectedDeviceTypes.join(',')) {
        return;
      }

      // if all device types are selected, we switch back to selectedDeviceTypes and unselect all device types
      if (allSelected) {
        for (const item of Object.values(deviceTypeList)) {
          item.selected = false;
        }
      }

      // if all device types are selected, the selected device types can be blank since select all device types will be true
      // if all or no device types are selected, switch back to select all device types
      this.setState({
        selectedDeviceTypes: allSelected ? [] : selectedDeviceTypes,
        selectAllDeviceTypes: allSelected || selectedDeviceTypes.length === 0,
        deviceTypeList: allSelected
          ? deviceTypeList
          : isWholeList
          ? selectedValues
          : this.state.deviceTypeList,
        madeFilterChange: true,
        forceOpenDeviceTypeList: false,
      });
    };

    handleBidderGroupsAcceptClick = (selectedValues: IMenuList): void => {
      const bidderGroupsList = cloneDeep(this.state.bidderGroupsList);
      const selectedBidderIDs = [] as string[];
      for (const item of Object.values(selectedValues).filter((item: IListItem) => item.selected)) {
        if (item.siteIDs) {
          // site ids are bidder ids in this case
          selectedBidderIDs.push(...item.siteIDs);
        }
      }

      this.setState({
        selectedBidderIDs: selectedBidderIDs.sort(),
        bidderGroupsList,
        madeFilterChange: true,
        forceOpenBidderGroupsList: false,
        selectAllBidders: selectedBidderIDs.length === 0,
        selectByBidderGroupID: selectedBidderIDs.length !== 0,
      });
    };

    handleViewAllChannels = (
      selectAllChannels: boolean,
      selectBySiteID: boolean,
      selectByChannelGroupID: boolean,
    ) => {
      // if no change, return
      if (
        selectAllChannels === this.state.selectAllChannels &&
        selectBySiteID === this.state.selectBySiteID &&
        selectByChannelGroupID === this.state.selectByChannelGroupID
      ) {
        return;
      }
      this.setState(
        {
          madeFilterChange: true,
          selectAllChannels,
          selectBySiteID,
          selectByChannelGroupID,
          forceOpenChannelsList: selectBySiteID,
          forceOpenChannelGroupsList: selectByChannelGroupID,
          selectedSiteIDs: selectAllChannels ? [] : this.state.selectedSiteIDs,
        },
        () => {
          this.generateBidderList();
          // if moving from select all to select sites but no site ids are selected or vice versa, no need to reload
          if (this.state.selectedSiteIDs.length === 0) {
            return;
          }
        },
      );
    };

    // toggles between bidder
    // will force open the dropdown if not selecting all bidders and selecting that dropdown
    handleViewAllBidders = (
      selectAllBidders: boolean,
      selectByBidderID: boolean,
      selectByBidderGroupID: boolean,
    ) => {
      // if no change, return
      if (
        selectAllBidders === this.state.selectAllBidders &&
        selectByBidderID === this.state.selectByBidderID &&
        selectByBidderGroupID === this.state.selectByBidderGroupID
      ) {
        return;
      }

      this.setState(
        {
          madeFilterChange: true,
          selectAllBidders,
          selectByBidderID,
          selectByBidderGroupID,
          forceOpenBiddersList: !selectAllBidders && selectByBidderID,
          forceOpenBidderGroupList: !selectAllBidders && selectByBidderGroupID,
          selectedBidderIDs: selectAllBidders ? [] : this.state.selectedBidderIDs,
        },
        () => {
          // if moving from select all to select bidders but no bidders ids are selected or vice versa, no need to reload
          if (this.state.selectedBidderIDs.length === 0) {
            return;
          }
        },
      );
    };

    handleViewAllDeviceTypes = (selectAllDeviceTypes: boolean) => {
      // if no change, return
      if (selectAllDeviceTypes === this.state.selectAllDeviceTypes) {
        return;
      }

      this.setState({
        madeFilterChange:
          selectAllDeviceTypes ||
          (!selectAllDeviceTypes && !!this.state.selectedDeviceTypes.length),
        selectAllDeviceTypes,
        forceOpenDeviceTypeList: !selectAllDeviceTypes,
      });
    };

    handleDensityRangeChangeAgg = (densityRangeAgg: DateGroup): void => {
      if (densityRangeAgg === DateGroupDays) {
        this.setState(
          {
            densityRangeAgg,
            // switch to last 7 days and last last 7 days for date ranges
            calStartDate: getStartDate(this.state.timezone, 6, 'day').startOf('day'),
            calEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            calDisplayStartDate: getStartDate(this.state.timezone, 6, 'day').startOf('day'),
            calDisplayEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            compStartDate: getStartDate(this.state.timezone, 13, 'day').startOf('day'),
            compEndDate: getStartDate(this.state.timezone, 7, 'day').endOf('day'),
            compDisplayStartDate: getStartDate(this.state.timezone, 13, 'day').startOf('day'),
            compDisplayEndDate: getStartDate(this.state.timezone, 7, 'day').endOf('day'),
            range1Selected: 'Last 7 Days',
            range1DisplaySelected: 'Last 7 Days',
          },
          () => this.loadDensityData(),
        );
      } else if (densityRangeAgg === DateGroupWeeks) {
        const daysFromSunday = moment().tz(this.state.timezone).weekday();
        this.setState(
          {
            densityRangeAgg,
            // switch to this week and last week
            calStartDate: getStartDate(this.state.timezone, daysFromSunday, 'day').startOf('day'),
            calEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            calDisplayStartDate: getStartDate(this.state.timezone, daysFromSunday, 'day').startOf(
              'day',
            ),
            calDisplayEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            compStartDate: getStartDate(this.state.timezone, daysFromSunday + 7, 'day').startOf(
              'day',
            ),
            compEndDate: getStartDate(this.state.timezone, daysFromSunday + 1, 'day').endOf('day'),
            compDisplayStartDate: getStartDate(
              this.state.timezone,
              daysFromSunday + 7,
              'day',
            ).startOf('day'),
            compDisplayEndDate: getStartDate(this.state.timezone, daysFromSunday + 1, 'day').endOf(
              'day',
            ),
            range1Selected: 'Last Week (Sun-Sat)',
            range1DisplaySelected: 'Last Week (Sun-Sat)',
          },
          () => this.loadDensityData(),
        );
      } else if (densityRangeAgg === DateGroupMonths) {
        this.setState(
          {
            densityRangeAgg,
            // switch to this month and last month
            calStartDate: getStartDate(this.state.timezone, 0, 'day').startOf('month'),
            calEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            calDisplayStartDate: getStartDate(this.state.timezone, 0, 'day').startOf('month'),
            calDisplayEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            compStartDate: getStartDate(this.state.timezone, 0, 'day')
              .startOf('month')
              .add(-1, 'months'),
            compEndDate: getStartDate(this.state.timezone, 0, 'day')
              .endOf('month')
              .add(-1, 'months'),
            compDisplayStartDate: getStartDate(this.state.timezone, 0, 'day')
              .startOf('month')
              .add(-1, 'months'),
            compDisplayEndDate: getStartDate(this.state.timezone, 0, 'day')
              .endOf('month')
              .add(-1, 'months'),
            range1Selected: 'Last Month',
            range1DisplaySelected: 'Last Month',
          },
          () => this.loadDensityData(),
        );
      } else {
        this.setState(
          {
            densityRangeAgg,
          },
          () => this.loadDensityData(),
        );
      }
    };

    findCompareStartDate = (timezone: string): moment.Moment => {
      const calDisplayStartDate = cloneDeep(this.state.calDisplayStartDate);
      const calEndDate = cloneDeep(this.state.calEndDate);
      if (
        this.state.range1Selected === 'This Week(Sun-Today)' ||
        this.state.range1Selected === 'Last Week(Sun-Sat)'
      ) {
        return moment(calDisplayStartDate).tz(timezone).subtract(1, 'week');
      } else if (
        this.state.range1Selected === 'Last Month' ||
        this.state.range1Selected === 'This Month'
      ) {
        return moment(calDisplayStartDate).tz(timezone).subtract(1, 'month');
      } else {
        return moment(calDisplayStartDate)
          .tz(timezone)
          .subtract(1 + calEndDate.diff(calDisplayStartDate, 'days'), 'days')
          .startOf('day');
      }
    };

    findCompareEndDate = (timezone: string): moment.Moment => {
      const calDisplayEndDate = cloneDeep(this.state.calDisplayEndDate);
      const calDisplayStartDate = cloneDeep(this.state.calDisplayStartDate);
      if (
        this.state.range1Selected === 'This Week(Sun-Today)' ||
        this.state.range1Selected === 'Last Week(Sun-Sat)'
      ) {
        return moment(calDisplayEndDate).tz(timezone).subtract(1, 'week');
      } else if (
        this.state.range1Selected === 'Last Month' ||
        this.state.range1Selected === 'This Month'
      ) {
        return moment(calDisplayEndDate).tz(timezone).subtract(1, 'month');
      } else {
        return moment(calDisplayStartDate).tz(timezone).subtract(1, 'days').endOf('day');
      }
    };

    // clears filters and reloads data
    handleClearFilters = (insideDropdown: boolean) => {
      // clear site list
      const siteList = cloneDeep(this.state.siteList);
      for (const item of Object.values(siteList)) {
        item.selected = false;
      }

      // clear bidder list
      const bidderList = cloneDeep(this.state.bidderList);
      for (const item of Object.values(bidderList)) {
        item.selected = false;
      }

      // clear bidder group list
      const bidderGroupsList = cloneDeep(this.state.bidderGroupsList);
      for (const item of Object.values(bidderGroupsList)) {
        item.selected = false;
      }

      // clear channel group list
      const channelGroupsList = cloneDeep(this.state.channelGroupsList);
      for (const item of Object.values(channelGroupsList)) {
        item.selected = false;
      }

      // clear device type list
      const deviceTypeList = cloneDeep(this.state.deviceTypeList);
      for (const item of Object.values(deviceTypeList)) {
        item.selected = false;
      }

      this.setState(
        {
          selectedSiteIDs: [],
          selectedBidderIDs: [],
          siteList,
          bidderList,
          bidderGroupsList,
          channelGroupsList,
          deviceTypeList,
          selectAllBidders: true,
          selectAllChannels: true,
          selectAllDeviceTypes: true,
          selectByBidderGroupID: false,
          selectByBidderID: false,
          selectByChannelGroupID: false,
          selectBySiteID: false,
          dealFilterOff: true,
          madeFilterChange: true,
        },
        () => {
          if (!insideDropdown) {
            this.loadData();
          }
        },
      );
    };

    loadData = () => {
      const { selectedSiteIDs } = this.state;

      const queryDuration = this.state.calStartDate.diff(this.state.calEndDate, 'days');

      this.setState(
        {
          madeFilterChange: false,
          slowLoad: selectedSiteIDs.length > 10 || queryDuration > 14,
        },
        () => {
          this.loadDensityData();
        },
      );
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
        snackbarNoData: false,
      });
    };

    // ########################## RENDER FUNCTIONS ##########################
    renderTop() {
      return (
        <AppBar
          position='sticky'
          elevation={0}
          style={{
            backgroundColor,
            color: blackColor,
          }}
        >
          <Grid container direction='column' style={{ minHeight: 70 }}>
            {this.renderTabs()}
            <Grid item>
              <Divider
                variant='fullWidth'
                style={{ height: '1px', marginTop: '3px', marginBottom: '3px' }}
                orientation='horizontal'
              />
              {this.renderFilters()}
              <Divider
                variant='fullWidth'
                style={{ height: '1px', marginTop: '3px', marginBottom: '3px' }}
                orientation='horizontal'
              />
            </Grid>
          </Grid>
        </AppBar>
      );
    }

    tabProps = (tabNumber: number) => {
      return {
        id: `simple-tab-${tabNumber}`,
        'aria-controls': `simple-tabpanel-${tabNumber}`,
      };
    };

    // @ts-ignore
    handleTabChange = (event: React.SyntheticEvent, tabNumber: number) => {
      const { currentTab, currentParty } = this.state;
      this.trackEvent(
        tabNumber === 0
          ? 'Measurement Dashboard - Click Inventory Data Tab'
          : 'Measurement Dashboard - Click Advertiser Data Tab',
      );
      if (currentTab === tabNumber) {
        return;
      }
      this.setState(
        {
          currentParty: currentParty === 'First Party' ? 'Third Party' : 'First Party',
          currentTab: tabNumber,
        },
        () => this.handleClearFilters(false),
      );
    };

    renderTabs() {
      const tabs = [];
      if (
        isFirstPartyEnabled(this.props.session.publisher) ||
        hasRoleSuperUser(this.props.user.user)
      ) {
        tabs.push('Inventory Data');
      }
      if (
        this.props.session.publisher.EnableIASThirdPartyVASTTracking === EBool.EBool_TRUE ||
        hasRoleSuperUser(this.props.user.user)
      ) {
        tabs.push('Advertiser Data');
      }
      return (
        <Grid item style={{ marginBottom: '4px' }}>
          <Box style={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabsWithBlueIndicator
              value={this.state.currentTab}
              onChange={this.handleTabChange}
              aria-label='basic tabs example'
            >
              {tabs.map((tab, index) => {
                return (
                  <Tab
                    label={tab}
                    style={{ color: this.state.currentTab === index ? '#5D66FA' : '' }}
                    {...this.tabProps(index)}
                  />
                );
              })}
            </TabsWithBlueIndicator>
          </Box>
        </Grid>
      );
    }

    renderFilters() {
      const {
        calEndDate,
        calStartDate,
        compStartDate,
        compEndDate,
        calDisplayEndDate,
        calDisplayStartDate,
        compDisplayStartDate,
        compDisplayEndDate,
        calFocusedInput,
        compFocusedInput,
        compareEnabled,
        range1Selected,
        range2Selected,
        range1DisplaySelected,
        range2DisplaySelected,
        timezone,
      } = this.state;
      // moment uses machine's timezone which needs to be adjusted to timezone in state
      // calendar quick select options have to be shifted 1 back where appropriate
      // since there is no current day data
      const calendarQuickSelectorOptions: ICalendarQuickSelectorOptions[] = [
        {
          title: 'Yesterday',
          date1: () => moment().tz(timezone).subtract(1, 'days'),
          date2: () => moment().tz(timezone).subtract(1, 'days'),
        },
        {
          title: 'This Week(Sun-Yesterday)',
          date1: () => moment().tz(timezone).subtract(moment().tz(timezone).format('d'), 'days'),
          date2: () => moment().tz(timezone).subtract(1, 'days'),
        },
        {
          title: 'Last 7 Days',
          date1: () => moment().tz(timezone).subtract(7, 'days'),
          date2: () => moment().tz(timezone).subtract(1, 'days'),
        },
        {
          title: 'Last Week (Sun-Sat)',
          date1: () =>
            moment()
              .tz(timezone)
              .subtract(parseInt(moment().tz(timezone).format('d'), 10) + 7, 'days'),
          date2: () =>
            moment()
              .tz(timezone)
              .subtract(parseInt(moment().tz(timezone).format('d'), 10) + 1, 'days'),
        },
        {
          title: 'This Month',
          date1: () => moment().tz(timezone).startOf('month'),
          date2: () => moment().tz(timezone).subtract(1, 'days'),
        },
        {
          title: 'Last 30 Days',
          date1: () => moment().tz(timezone).subtract(30, 'days'),
          date2: () => moment().tz(timezone).subtract(1, 'days'),
        },
        {
          title: 'Last Month',
          date1: () => moment().tz(timezone).subtract(1, 'months').date(1),
          date2: () =>
            moment()
              .tz(timezone)
              .subtract(1, 'months')
              .date(moment().tz(timezone).subtract(1, 'months').daysInMonth()),
        },
      ];
      const calendarQuickSelectorCustomOptions: ICalendarQuickSelectorOptions[] = [
        {
          title: ' Days Up To Yesterday',
          date1: (e: number | string) => moment().tz(timezone).subtract(e, 'days'),
          date2: () => moment().tz(timezone).subtract(1, 'days'),
        },
      ];
      const compareQuickSelectorOptions: ICalendarQuickSelectorOptions[] = [
        {
          title: 'Previous Period',
          date1: () => this.findCompareStartDate(timezone),
          date2: () => this.findCompareEndDate(timezone),
        },
        {
          title: 'Last Month',
          date1: () => moment(calDisplayStartDate).tz(timezone).subtract(1, 'months'),
          date2: () => moment(calDisplayEndDate).tz(timezone).subtract(1, 'months'),
        },
      ];
      const compareQuickSelectorCustomOptions: ICalendarQuickSelectorOptions[] = [
        {
          title: ' Days Ago, Same Period',
          date1: (value: string) =>
            moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'days'),
          date2: (value: string) =>
            moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'days'),
        },
        {
          title: ' Weeks Ago, Same Period',
          date1: (value: string) =>
            moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'weeks'),
          date2: (value: string) =>
            moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'weeks'),
        },
        {
          title: ' Months Ago, Same Period',
          date1: (value: string) =>
            moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'months'),
          date2: (value: string) =>
            moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'months'),
        },
      ];
      const isCalDayHighlighted = (day: moment.Moment): boolean => {
        if (!compareEnabled) {
          return false;
        }
        return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
          compDisplayStartDate,
          compDisplayEndDate,
          'day',
          '[]',
        );
      };
      const isCompDayHighlighted = (day: moment.Moment): boolean => {
        return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
          calDisplayStartDate,
          calDisplayEndDate,
          'day',
          '[]',
        );
      };
      const activeFilterCount = this.countActiveFilters();
      return (
        <Grid
          item
          container
          direction='row'
          justify='space-between'
          alignItems='center'
          spacing={2}
          style={{ margin: -8, minHeight: 65 }}
        >
          {/* left - filter button */}
          <Grid item style={{ marginLeft: 12 }}>
            <Grid container alignItems='center'>
              <Grid item>{this.renderFilterButton()}</Grid>
              {activeFilterCount !== 0 && (
                <Grid item onClick={() => this.handleClearFilters(false)}>
                  <Typography
                    style={{
                      marginLeft: 10,
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      color: '#5D66FA',
                      textTransform: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Clear
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              direction='row'
              justify='flex-end'
              alignItems='center'
              spacing={2}
              style={{ width: 'auto' }}
            >
              <Grid item>
                <Grid
                  container
                  direction='row'
                  justify='flex-end'
                  alignItems='center'
                  spacing={1}
                  style={{ width: 'auto' }}
                >
                  <Grid item justify='flex-start'>
                    <Typography style={{ fontSize: 12, fontWeight: 500 }}>Period</Typography>
                  </Grid>
                  <Grid item justify='flex-start'>
                    <Typography style={{ fontSize: 12 }}>{range1DisplaySelected}</Typography>
                  </Grid>
                  <Grid item>
                    <Calendar
                      startDate={calStartDate}
                      endDate={calEndDate}
                      calStartDate={calDisplayStartDate}
                      calEndDate={calDisplayEndDate}
                      startDateID='startDate'
                      endDateID='endDate'
                      rangeSelected={range1Selected}
                      handleRangeSelected={this.handleChangeAggRangeSelected}
                      handleCalendarDateChange={this.handleCalendarDateChange(false)}
                      handleDateRangeChange={this.handleDateRangeChange}
                      quickSelectorOptions={calendarQuickSelectorOptions}
                      quickSelectorCustomOptions={calendarQuickSelectorCustomOptions}
                      isDayHighlighted={isCalDayHighlighted}
                      isCompareCalendar={false}
                      focusedInput={calFocusedInput}
                      excludeToday={true}
                      dateFormat={formatWithoutHoursMins(this.props.session.publisher.Localization)}
                      handleFocusedInputChange={this.handleCalendarFocusedInputChange(false)}
                      trackOnClose={() => this.trackEvent('Measurement Dashboard - Open Calendar')}
                      timezone={timezone}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid
                  container
                  direction='row'
                  justify='flex-end'
                  alignItems='center'
                  spacing={1}
                  style={{ width: 'auto' }}
                >
                  <Grid item>
                    <Grid container justify='flex-start' alignItems='center' direction='row'>
                      <Grid item>
                        <Checkbox
                          size='small'
                          checked={compareEnabled}
                          onChange={this.handleCompareEnabled}
                        />
                      </Grid>
                      <Grid item>
                        <Typography style={{ fontSize: 12 }}>{range2DisplaySelected}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid container direction='column'>
                      <Calendar
                        startDate={compStartDate}
                        endDate={compEndDate}
                        calStartDate={compDisplayStartDate}
                        calEndDate={compDisplayEndDate}
                        startDateID='startDate_Compare'
                        endDateID='endDate_Compare'
                        rangeSelected={range2Selected}
                        handleRangeSelected={this.handleChangeAggRangeSelected}
                        handleCalendarDateChange={this.handleCalendarDateChange(true)}
                        handleDateRangeChange={this.handleDateRangeChange}
                        quickSelectorOptions={compareQuickSelectorOptions}
                        quickSelectorCustomOptions={compareQuickSelectorCustomOptions}
                        isDayHighlighted={isCompDayHighlighted}
                        isCompareCalendar={true}
                        focusedInput={compFocusedInput}
                        dateFormat={formatWithoutHoursMins(
                          this.props.session.publisher.Localization,
                        )}
                        handleFocusedInputChange={this.handleCalendarFocusedInputChange(true)}
                        timezone={timezone}
                        trackOnClose={() =>
                          this.trackEvent('Measurement Dashboard - Open Compare Calendar')
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }

    getBidderIDsForQuery = (): number[] => {
      const bidderIDs = this.state.selectAllBidders
        ? []
        : this.state.selectedBidderIDs.map((id) => parseInt(id, 10));
      return bidderIDs;
    };

    getSiteIDsForQuery = (): number[] => {
      const siteIDs = this.state.selectAllChannels
        ? []
        : this.state.selectedSiteIDs.map((id) => parseInt(id, 10));
      return siteIDs;
    };

    getDeviceTypesForQuery = (): number[] => {
      let devicetypes = this.state.selectAllDeviceTypes
        ? []
        : this.state.selectedDeviceTypes.map((dT) => parseInt(dT, 10));
      if (
        this.state.selectedDeviceTypes.length ===
        getFirstOrThirdPartyDeviceTypes(this.state.currentParty).length
      ) {
        devicetypes = []; // select all
      }
      return devicetypes;
    };

    countActiveFilters = (): number => {
      let activeFilterCount = 0;
      if (this.state.selectedSiteIDs.length && !this.state.selectAllChannels) {
        activeFilterCount++;
      }
      if (this.state.selectedBidderIDs.length && !this.state.selectAllBidders) {
        activeFilterCount++;
      }
      if (this.state.selectedDeviceTypes.length && !this.state.selectAllDeviceTypes) {
        activeFilterCount++;
      }
      return activeFilterCount;
    };

    isChannelFilterActive = (): boolean => {
      const { selectAllChannels, selectBySiteID, selectByChannelGroupID } = this.state;
      return !selectAllChannels && (selectBySiteID || selectByChannelGroupID);
    };

    isBidderFilterActive = (): boolean => {
      const { selectAllBidders, selectByBidderID, selectByBidderGroupID } = this.state;
      return !selectAllBidders && (selectByBidderID || selectByBidderGroupID);
    };

    isDeviceTypeFilterActive = (): boolean => {
      const { selectAllDeviceTypes, selectedDeviceTypes } = this.state;
      return !selectAllDeviceTypes && selectedDeviceTypes.length > 0;
    };

    // filter menu render functions
    renderMenuTitle = (insideDropdown: boolean) => {
      const activeFilterCount = this.countActiveFilters();
      return (
        <Grid container direction='row' alignItems='center'>
          <Grid item>
            <Typography
              style={{
                marginLeft: -5,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'black',
              }}
            >
              FILTERS
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: 10 }}>
            <img src='/assets/shared/svg/FilterDropdownRoot.svg' />
          </Grid>
          {activeFilterCount !== 0 && (
            <Grid item style={{ marginLeft: 25 }}>
              <FilterActiveChip
                label={
                  activeFilterCount === 1
                    ? '1 Active Filter'
                    : `${activeFilterCount} Active Filters`
                }
              />
            </Grid>
          )}
          {insideDropdown && activeFilterCount !== 0 && (
            <Grid item style={{ marginLeft: 10 }} onClick={() => this.handleClearFilters(true)}>
              <Typography
                style={{
                  marginLeft: 30,
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  color: '#4952FF',
                  textTransform: 'none',
                }}
              >
                Clear
              </Typography>
            </Grid>
          )}
        </Grid>
      );
    };

    renderChannelRow = () => {
      const {
        siteList,
        channelGroupsList,
        selectBySiteID,
        selectByChannelGroupID,
        selectAllChannels,
        forceOpenChannelsList,
        forceOpenChannelGroupsList,
      } = this.state;

      // determine if the other filters are active
      const bidderFilterActive = this.isBidderFilterActive();
      const deviceTypeFilterActive = this.isDeviceTypeFilterActive();

      // disable the channel filter if the deal filter is selected
      // or if devicetype is selected with bider
      const disableChannelFilter = bidderFilterActive && deviceTypeFilterActive;

      return (
        <Grid item style={{ width: '100%' }}>
          <Tooltip
            title={disableChannelFilter ? 'Filter inactive with current filter combination' : ''}
          >
            <Grid container direction='row' alignItems='center'>
              <Grid item xs={2}>
                <Typography style={{ fontWeight: 500, fontSize: 12 }}>Supply</Typography>
              </Grid>
              <Grid item xs={1}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>
                    <Radio
                      size='small'
                      checked={selectAllChannels}
                      disabled={disableChannelFilter}
                      // @ts-ignore
                      onChange={() => this.handleViewAllChannels(true, false, false)}
                    />
                  </Grid>
                  <Grid item>
                    <Typography
                      style={{ fontSize: 12, color: selectAllChannels ? 'black' : '#cecdcf' }}
                    >
                      All
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>
                    <Radio
                      size='small'
                      checked={selectBySiteID}
                      disabled={disableChannelFilter}
                      onChange={() => this.handleViewAllChannels(false, true, false)}
                    />
                  </Grid>
                  <Grid item>
                    <MaterialSearchDropdownMulti
                      width='150px'
                      fontSize='12px'
                      menuTitle=''
                      list={siteList}
                      onChange={(list: IMenuList) => {
                        this.setState({
                          siteList: list,
                        });
                      }}
                      disabled={selectAllChannels || disableChannelFilter}
                      handleMenuAcceptClick={(src: IMenuList) => {
                        this.handleChannelAcceptClick(src);
                      }}
                      menuTitleOverride={'Select Channels'}
                      selectLimit={-1}
                      forceOpen={forceOpenChannelsList}
                      onlyShowNumberWhenClosed={true}
                      maxHeight={350}
                      enableFixed={true}
                      selectAll={true}
                      showPrefixID
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>
                    <Radio
                      size='small'
                      checked={selectByChannelGroupID}
                      disabled={disableChannelFilter}
                      onChange={() => this.handleViewAllChannels(false, false, true)}
                    />
                  </Grid>
                  <Grid item>
                    <MaterialSearchDropdownMulti
                      width='150px'
                      fontSize='12px'
                      menuTitle='channel group'
                      list={channelGroupsList}
                      onChange={(channelGroupsList: IMenuList) => {
                        this.setState({
                          channelGroupsList,
                        });
                      }}
                      disabled={selectAllChannels || disableChannelFilter}
                      handleMenuAcceptClick={(src: IMenuList) => {
                        this.handleChannelGroupsAcceptClick(src);
                      }}
                      menuTitleOverride={'Select a channel group'}
                      selectLimit={1}
                      forceOpen={forceOpenChannelGroupsList}
                      maxHeight={350}
                      enableFixed={true}
                      selectAll={false}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Tooltip>
        </Grid>
      );
    };

    renderBidderRow = () => {
      const {
        bidderList,
        bidderGroupsList,
        bidderListIsLoading,
        selectAllBidders,
        selectByBidderID,
        selectByBidderGroupID,
        forceOpenBiddersList,
        forceOpenBidderGroupList,
      } = this.state;
      const selectByName = !selectAllBidders && selectByBidderID;

      // determine if the other filters are active
      const deviceTypeFilterActive = this.isDeviceTypeFilterActive();

      // bidder and bidder group should be disabled when device type is selected
      const disableBidderFilter = deviceTypeFilterActive;

      return (
        <Grid item style={{ width: '100%' }}>
          <Tooltip title={''}>
            <Grid container direction='row' alignItems='center' spacing={0}>
              <Grid item xs={2}>
                <Typography style={{ fontWeight: 500, fontSize: 12 }}>Bidders</Typography>
              </Grid>
              <Grid item xs={1}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>
                    <Radio
                      size='small'
                      checked={selectAllBidders}
                      // @ts-ignore
                      onChange={() => this.handleViewAllBidders(true, false, false)}
                    />
                  </Grid>
                  <Grid item>
                    <Typography
                      style={{ fontSize: 12, color: selectAllBidders ? 'black' : '#cecdcf' }}
                    >
                      All
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {/* Bidders by name (headerbidders) */}
              <Grid item direction='row' alignItems='center' xs={4}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>
                    <Radio
                      size='small'
                      checked={selectByName}
                      disabled={disableBidderFilter}
                      // @ts-ignore
                      onChange={() => this.handleViewAllBidders(false, true, false)}
                    />
                  </Grid>
                  <Grid item>
                    <MaterialSearchDropdownMulti
                      fontSize='12px'
                      menuTitle='Bidders'
                      width='150px'
                      selectAll={false}
                      list={bidderList}
                      onChange={(list: IMenuList) => {
                        // set state
                        this.setState({
                          bidderList: list,
                        });
                      }}
                      disabled={!selectByName} // || disableBidderFilter}
                      handleMenuAcceptClick={(src: IMenuList) => {
                        // @ts-ignore
                        this.handleBidderAcceptClick(src);
                      }}
                      menuTitleOverride='By Name'
                      selectLimit={20}
                      forceOpen={forceOpenBiddersList}
                      onlyShowNumberWhenClosed={true}
                      leftRightDropdownEnabled={true}
                      maxHeight={350}
                      loading={bidderListIsLoading}
                      enableFixed={true}
                      showPrefixID
                    />
                  </Grid>
                </Grid>
              </Grid>
              {/* Bidders by bidder group*/}
              <Grid item direction='row' alignItems='center' xs={4}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>
                    <Radio
                      size='small'
                      checked={selectByBidderGroupID}
                      disabled={disableBidderFilter}
                      // @ts-ignore
                      onChange={() => this.handleViewAllBidders(false, false, true)}
                    />
                  </Grid>
                  <Grid item>
                    <MaterialSearchDropdownMulti
                      width='150px'
                      fontSize='12px'
                      menuTitle='bidder group'
                      list={bidderGroupsList}
                      onChange={(bidderGroupsList: IMenuList) => {
                        this.setState({
                          bidderGroupsList,
                        });
                      }}
                      disabled={!selectByBidderGroupID || disableBidderFilter}
                      handleMenuAcceptClick={(src: IMenuList) => {
                        this.handleBidderGroupsAcceptClick(src);
                      }}
                      menuTitleOverride={'Select a bidder group'}
                      selectLimit={1}
                      forceOpen={forceOpenBidderGroupList}
                      maxHeight={350}
                      enableFixed={true}
                      selectAll={false}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Tooltip>
        </Grid>
      );
    };

    renderDeviceTypeRow = () => {
      const { selectAllDeviceTypes, selectAllBidders, selectByBidderID, selectByBidderGroupID } =
        this.state;

      const selectByName = !selectAllDeviceTypes;

      // determine if the other filters are active
      const bidderFilterActive = !selectAllBidders && (selectByBidderID || selectByBidderGroupID);

      // disable device type if deal filter is selected
      // of if bidderID or bidderGroupID are selected
      const disableDeviceTypeFilter = bidderFilterActive;

      return (
        <Grid item style={{ width: '100%' }}>
          <Tooltip
            title={disableDeviceTypeFilter ? 'Filter inactive with current filter combination' : ''}
          >
            <Grid container direction='row' alignItems='center' spacing={0}>
              <Grid item xs={2}>
                <Typography style={{ fontWeight: 500, fontSize: 12 }}>Device Type</Typography>
              </Grid>
              <Grid item xs={1}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>
                    <Radio
                      size='small'
                      checked={selectAllDeviceTypes}
                      disabled={disableDeviceTypeFilter}
                      // @ts-ignore
                      onChange={() => this.handleViewAllDeviceTypes(true)}
                    />
                  </Grid>
                  <Grid item>
                    <Typography
                      style={{ fontSize: 12, color: selectAllDeviceTypes ? 'black' : '#cecdcf' }}
                    >
                      All
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {/* Device Types */}
              <Grid item direction='row' alignItems='center' xs={4}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>
                    <Radio
                      size='small'
                      checked={selectByName}
                      disabled={disableDeviceTypeFilter}
                      // @ts-ignore
                      onChange={() => this.handleViewAllDeviceTypes(false)}
                    />
                  </Grid>
                  <Grid item>
                    <MaterialSearchDropdownMulti
                      fontSize='12px'
                      menuTitle='Device Type'
                      width='150px'
                      selectAll={false}
                      list={this.state.deviceTypeList}
                      onChange={(list: IMenuList) => {
                        // set state
                        this.setState({
                          deviceTypeList: list,
                        });
                      }}
                      disabled={!selectByName || disableDeviceTypeFilter}
                      handleMenuAcceptClick={(src: IMenuList) => {
                        // @ts-ignore
                        this.handleDeviceTypeAcceptClick(src);
                      }}
                      menuTitleOverride='By Name'
                      selectLimit={5}
                      forceOpen={this.state.forceOpenDeviceTypeList}
                      onlyShowNumberWhenClosed={true}
                      leftRightDropdownEnabled={true}
                      maxHeight={350}
                      enableFixed={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Tooltip>
        </Grid>
      );
    };

    renderFilterButton = () => {
      const menuTitleRow = {
        closeOnClick: false,
        render: () => this.renderMenuTitle(true),
      };
      const channelRow = {
        closeOnClick: false,
        render: this.renderChannelRow,
      };
      const bidderRow = {
        closeOnClick: false,
        render: this.renderBidderRow,
      };
      const deviceTypeRow = {
        closeOnClick: false,
        render: this.renderDeviceTypeRow,
      };
      const menuButtonItems = [menuTitleRow, deviceTypeRow];
      if (this.state.currentParty === 'Third Party') {
        menuButtonItems.splice(1, 0, channelRow, bidderRow);
      }
      return (
        <MenuButton
          titleRender={() => this.renderMenuTitle(false)}
          items={menuButtonItems}
          minWidth={700}
          trackOnOpen={() => this.trackEvent('Measurement Dashboard - Open Filter Box')}
          onClose={() => {
            setTimeout(() => {
              // give time for menus to close and decide if the menu has changed
              if (this.state.madeFilterChange) {
                this.loadDensityData();
              }
            }, 500);
          }}
        />
      );
    };

    renderCardsAndGraphAgg() {
      const {
        calEndDate,
        calStartDate,
        compStartDate,
        compEndDate,
        compareEnabled,
        data1,
        data2,
        loading1,
        loading2,
        densityRangeAgg,
        dashboardCardDataDisplay,
        dashboardCardDataCompareDisplay,
        selectedMetrics,
      } = this.state;
      const { classes } = this.props;
      const isLoading = compareEnabled
        ? loading1 === LoadingStateLoading && loading2 === LoadingStateLoading
        : loading1 === LoadingStateLoading;
      return (
        <div style={{ marginTop: 5 }}>
          {isLoading && <LinearProgress style={{ width: '100%', height: 5, marginBottom: 7 }} />}
          <DashboardCards
            loaded={
              compareEnabled
                ? loading1 === LoadingStateHasData && loading2 === LoadingStateHasData
                : loading1 === LoadingStateHasData
            }
            loading={
              compareEnabled
                ? loading1 === LoadingStateLoading && loading2 === LoadingStateLoading
                : loading1 === LoadingStateLoading
            }
            entries={dashboardCardDataDisplay}
            entriesCompare={dashboardCardDataCompareDisplay}
            compareEnabled={compareEnabled}
            menuItemList={firstAndThirdPartyDashboardCardDropdownList}
            handleSwitchActiveMetric={this.handleSwitchActiveMetric}
            handleSwitchCardSelected={this.handleSwitchCardSelected}
            selectedMetricsColorMap={selectedMetricsColorMap}
            selectedMetrics={selectedMetrics}
          />
          <DashboardGraphV2
            graphTitle=''
            toggleLiveData={false}
            mergeLiveData={false}
            classes={classes}
            densityRange={densityRangeAgg}
            handleDensityRangeChange={this.handleDensityRangeChangeAgg}
            calStartDate={calStartDate}
            calEndDate={calEndDate}
            compStartDate={compStartDate}
            compEndDate={compEndDate}
            data1={data1}
            data2={data2}
            dataLive={[]}
            compareEnabled={compareEnabled}
            loaded={
              compareEnabled
                ? loading1 === LoadingStateHasData && loading2 === LoadingStateHasData
                : loading1 === LoadingStateHasData
            }
            loadingState1={loading1}
            loadingState2={loading2}
            metricsInfo={dashboardCardDataDisplay}
            selectedMetricsColorMap={selectedMetricsColorMap}
            currencySymbol='$'
            selectedMetrics={selectedMetrics}
            hourAggRangeDisabled={true}
            selectedMetricsDataToggle={{} as ISelectedMetrics}
            selectedMetricsGraphData={{} as IGraphDataTypes}
            handleMetricsChange={() => null}
            user={this.props.user.user}
          />
        </div>
      );
    }

    // #################### Advertiser Table Render/Functions ####################
    createTableDataPage = (data: IAnalytics[], query: Query<ITableDataItem>, user: IUser) => {
      return formatTableData(
        data.slice(query.page * query.pageSize, (query.page + 1) * query.pageSize),
        checkUserInfoMasked(user),
      );
    };

    advertiserTableQuery = (query: Query<ITableDataItem>): Promise<QueryResult<ITableDataItem>> => {
      return new Promise((resolve) => {
        this.setState(
          {
            loadingAdvertiserTable: LoadingStateLoading,
          },
          () => {
            const { calStartDate, calEndDate, densityRangeAgg, currentParty, timezone } =
              this.state;
            const start_date = formatDateStartDay(calStartDate);
            const end_date =
              this.generateEndDateForSnowflakeQuery(calEndDate).format('YYYY-MM-DDTHH:mm:ss');
            // query params
            const graph_id = currentParty === 'First Party' ? 'graph4.sql' : 'graph3.sql';
            const grouping = densityRangeAgg;
            const queryOrderBy = query.orderBy
              ? advertiserColumnToSQL[query.orderBy.field || 'TrackedAds']
              : 'tracked_ads';
            const querySort = query.orderDirection || 'desc';
            const queryParams = {
              Debug: true,
              GraphID: graph_id,
              StartDate: start_date,
              EndDate: end_date,
              Timezone: timezone,
              GroupingNumber: getDateGroupNumber(grouping),
              GroupBys: ['advertiser_name', 'advertiser_id'],
              Metrics: advertiserTableColumns
                .slice(1)
                .map((column) => advertiserColumnToSQL[column.field || 'TrackedAds']),
              DeviceTypes: this.getDeviceTypesForQuery(),
              TeamID: '15661',
              PublicaChannelIDs: currentParty === 'Third Party' ? this.getSiteIDsForQuery() : [],
              PublicaBidderIDs: currentParty === 'Third Party' ? this.getBidderIDsForQuery() : [],
              AnIDs: [+this.props.session.publisher.IASAnID],
              ReportOrderBy: `${queryOrderBy} ${querySort}`,
            } as IIASFirstAndThirdPartyRequestBody;
            /** * Main Graph Queries ***/
            getFirstAndThirdPartyIasData(queryParams, {
              publisher_id: this.props.session.publisher.ID,
            })
              .then((data) => {
                const rows = data[0];
                resolve({
                  data: this.createTableDataPage(rows, query, this.props.user.user),
                  page: query.page,
                  totalCount: rows.length,
                });
                this.setState({
                  loadingAdvertiserTable:
                    rows.length > 0 ? LoadingStateHasData : LoadingStateNoData,
                  advertiserTablePageSize: query.pageSize,
                });
              })
              .catch(() => {
                this.setState({
                  loadingAdvertiserTable: LoadingStateNoData,
                  snackbarError: false,
                  snackbarMessage: 'Error retrieving advertiser table data. Please retry.',
                });
              });
          },
        );
      });
    };

    renderTopAdvertisers() {
      const { classes } = this.props;

      const { advertiserTablePageSize } = this.state;

      return (
        <div>
          <section className={classes.section}>
            <MaterialTable
              style={{ paddingLeft: 20, paddingRight: 20 }}
              tableRef={this.advtertiserTableRef}
              title='Top Advertisers'
              options={{
                pageSize: advertiserTablePageSize,
                search: false,
                rowStyle: {
                  height: 70,
                  color:
                    this.state.loadingAdvertiserTable === LoadingStateLoading
                      ? 'lightgray'
                      : 'black',
                },
                loadingType: 'linear',
                thirdSortClick: false,
              }}
              localization={{
                body: {
                  emptyDataSourceMessage:
                    this.state.loadingAdvertiserTable === LoadingStateLoading
                      ? 'Loading query'
                      : 'No records to display',
                },
              }}
              columns={advertiserTableColumns}
              data={this.advertiserTableQuery}
            />
          </section>
        </div>
      );
    }

    render() {
      const { snackbarMessage, snackbarError, snackbarNoData, currentParty } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {this.renderTop()}
            {this.renderCardsAndGraphAgg()}
            {currentParty === 'Third Party' && this.renderTopAdvertisers()}
            <SnackbarView
              autoHideDuration={12000}
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.snackbarClose}
              messageColor={''}
              backgroundColor={''}
              closeColor={''}
              warning={!!snackbarNoData}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);

// Percentage change = (new_value - old_value) / old_value
const calculatePercentageChange = (val1: number, val2: number): string => {
  if (val1 === 0) {
    return '0.00%';
  }
  if (val2 === 0 && val1 === 0) {
    return '0.00%';
  }
  if (val2 === 0) {
    return '100%';
  }
  return (((val1 - val2) * 100) / val2).toFixed(2) + '%';
};

const generateDefaultDashboardCardDisplay = (): IFirstAndThirdPartyDashboardCardDataDisplay => {
  return {
    'Tracked Ads': {
      value: '',
      sign: 1,
      key: dashboardKeyTrackedAds,
      compareKey: dashboardCompareKeyTrackedAds,
      yAxisID: 'big',
      suffix: '',
    },
    'Impressions Rendered': {
      value: '',
      sign: 1,
      key: dashboardKeyImpressionsRendered,
      compareKey: dashboardCompareKeyImpressionsRendered,
      yAxisID: 'big',
      suffix: '',
    },
    'Render Rate': {
      value: '',
      sign: 1,
      key: dashboardKeyRenderRate,
      compareKey: dashboardCompareKeyRenderRate,
      yAxisID: 'big',
      suffix: '',
    },
    'Invalid Traffic Ads': {
      value: '',
      sign: 1,
      key: dashboardKeyInvalidTrafficAds,
      compareKey: dashboardCompareKeyInvalidTrafficAds,
      yAxisID: 'big',
      suffix: '',
    },
    'Invalid Traffic Rate': {
      value: '',
      sign: 1,
      key: dashboardKeyInvalidTrafficRate,
      compareKey: dashboardCompareKeyInvalidTrafficRate,
      yAxisID: 'big',
      suffix: '',
    },
    'Eligible Ads': {
      value: '',
      sign: 1,
      key: dashboardKeyEligibleAds,
      compareKey: dashboardCompareKeyEligibleAds,
      yAxisID: 'big',
      suffix: '',
    },
    'Measured Ads': {
      value: '',
      sign: 1,
      key: dashboardKeyMeasuredAds,
      compareKey: dashboardCompareKeyMeasuredAds,
      yAxisID: 'small',
      suffix: '',
    },
    'Measured Rate': {
      value: '',
      sign: 1,
      key: dashboardKeyMeasuredRate,
      compareKey: dashboardCompareKeyMeasuredRate,
      yAxisID: 'small',
      suffix: '',
    },
    'Viewable Impressions': {
      value: '',
      sign: 1,
      key: dashboardKeyViewableImpressions,
      compareKey: dashboardCompareKeyViewableImpressions,
      yAxisID: 'small',
      suffix: '',
    },
    'Viewable Rate': {
      value: '',
      sign: 1,
      key: dashboardKeyViewableRate,
      compareKey: dashboardCompareKeyViewableRate,
      yAxisID: 'small',
      suffix: '',
    },
    'Blocked Ads': {
      value: '',
      sign: 1,
      key: dashboardKeyBlockedAds,
      compareKey: dashboardCompareKeyBlockedAds,
      yAxisID: 'small',
      suffix: '',
    },
    'Blocked Rate': {
      value: '',
      sign: 1,
      key: dashboardKeyBlockedRate,
      compareKey: dashboardCompareKeyBlockedRate,
      yAxisID: 'small',
      suffix: '',
    },
    'AVOC GroupM Count': {
      value: '',
      sign: 1,
      key: dashboardKeyAudibleViewableCustomQ2,
      compareKey: dashboardCompareKeyAudibleViewableCustomQ2,
      yAxisID: 'small',
      suffix: '',
    },
    'AVOC Count': {
      value: '',
      sign: 1,
      key: dashboardKeyAudibleViewableIndStdQ4,
      compareKey: dashboardCompareKeyAudibleViewableIndStdQ4,
      yAxisID: 'small',
      suffix: '',
    },
    'AVOC GroupM Rate': {
      value: '',
      sign: 1,
      key: dashboardKeyAVOCGroupMRate,
      compareKey: dashboardCompareKeyAVOCGroupMRate,
      yAxisID: 'small',
      suffix: '',
    },
    'AVOC Rate': {
      value: '',
      sign: 1,
      key: dashboardKeyAVOCRate,
      compareKey: dashboardCompareKeyAVOCRate,
      yAxisID: 'small',
      suffix: '',
    },
  };
};
